/**
 * Kartkrig — phase 1.
 *
 * Two modes over the same map. Probe reads the terrain at a point. March plans
 * a route between two points across the real road network and reports how long
 * it would take — the thing the original interface mock promised: a formation,
 * a named route, an ETA in hours.
 *
 * There is still no game. No units, no economy, no zones of control. What this
 * proves is that real geography can answer operational questions.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import {
  fetchBasemaps,
  fetchHealth,
  type HealthReport,
} from "./map/api";
import {
  continueGame,
  createWorld,
  fetchCampaign,
  fetchWorld,
  orderMove,
  type ArrivalView,
  type CampaignReport,
  type MoveMode,
  type Unit,
  type WorldReport,
} from "./map/api";
import {
  formatRemaining,
  formationLabel,
  explainStatus,
  statusOf,
  strengthColour,
  symbolFromSidc,
  symbolSizeFor,
} from "./map/symbols";
import { findStacks, ringOffsets } from "./map/stack";
import { AMS_ATTRIBUTION, AMS_BOUNDS, AMS_LAYERS, AMS_MIN_ZOOM, amsTiles } from "./map/ams";
import { rangeRing, ringPulse } from "./map/range";
import { glide } from "./map/motion";
import {
  arrivalDuration,
  arrivalFrame,
  arrivalMarkup,
  seaTransitProgress,
  SEA_TRANSIT_GAME_S,
  transportShipMarkup,
  type ArrivalMode,
} from "./map/arrivals";
import { formatCampaignTime, operationPhase } from "./map/operation";
import type { ScreenPoint } from "./map/stack";
import {
  fetchClock,
  fetchBattle,
  fetchLog,
  fetchZoc,
  resetWorlds,
  setGameCode,
  mergeUnits,
  setCombatStance,
  airStrike,
  setPause,
  setRate as apiSetRate,
  setSettings as apiSetSettings,
  type ZocSettings,
  splitUnit,
  RATES,
  type BattleReport,
  type ClockReport,
  type LogReport,
  type ZocReport,
} from "./map/api";
import { BUILD, BUILD_NOTE } from "./build";
import { currentPhase, medalIcon, medalStrip, phaseHighlight, phaseProgress } from "./map/phases";
import {
  buildBlankStyle,
  buildStyle,
  ROADS_OVER_IMAGERY,
  ROADS_OVER_MAP,
} from "./map/style";
import {
  CAMERA_BOUNDS,
  MAX_ZOOM,
  SICILY_BOUNDS,
  boundsCentre,
  clampCentre,
  minZoomForBounds,
} from "./map/theatre";

/**
 * Where the map opens.
 *
 * Derived from the theatre rather than written down. The camera cannot leave
 * Sicily, so an opening view anywhere else would be clamped on the first frame
 * and read as a bug — which is exactly what the old Dalarna coordinates would
 * now do. The zoom here only covers the frames before the map knows its own
 * size; the real opening zoom is computed on load.
 */
const [INITIAL_LNG, INITIAL_LAT] = boundsCentre(SICILY_BOUNDS);
const INITIAL_VIEW = { lng: INITIAL_LNG, lat: INITIAL_LAT, zoom: 8.5 };

interface ArrivalAnimation {
  mode: ArrivalMode;
  startedAt: number;
  target: { lat: number; lon: number };
  finalHtml: string;
  finalState: string;
}

interface TransportClock {
  gameTimeS: number;
  sampledAt: number;
  rate: number;
}

/**
 * The reach settings, as a table rather than five hand-written rows.
 *
 * Ranges are the client's own and deliberately narrower than the server's
 * clamps: a slider should offer useful values, while the server still has to
 * defend against anything at all arriving over HTTP. The two are not a
 * duplicated rule — one shapes a control, the other guards a database.
 */
const ZOC_FIELDS = [
  {
    key: "base_radius_m" as const,
    group: "base" as const,
    label: "Company circle",
    min: 500,
    max: 12000,
    step: 250,
    format: (v: number) => `${(v / 1000).toFixed(1)} km`,
  },
  {
    key: "step_per_echelon" as const,
    group: "base" as const,
    label: "Per echelon",
    min: 0,
    max: 1,
    step: 0.05,
    format: (v: number) => `+${Math.round(v * 100)}%`,
  },
  {
    key: "spread_hours" as const,
    group: "spread" as const,
    label: "Time given",
    min: 0.25,
    max: 6,
    step: 0.25,
    format: (v: number) => `${v} h`,
  },
  {
    key: "road_kmh" as const,
    group: "spread" as const,
    label: "Along roads",
    min: 5,
    max: 100,
    step: 5,
    format: (v: number) => `${v} km/h`,
  },
  {
    key: "terrain_kmh" as const,
    group: "spread" as const,
    label: "Open ground",
    min: 1,
    max: 30,
    step: 1,
    format: (v: number) => `${v} km/h`,
  },
];

/**
 * A diagonal hatch, built as pixels because MapLibre fills with images.
 *
 * There is no CSS here — a `fill-pattern` takes a registered image, so the
 * stripes are drawn once into a canvas at start-up and handed over as raw RGBA.
 * 8×8 at pixelRatio 2 tiles to roughly 4 device pixels per stripe: visible at a
 * glance, fine enough that it reads as texture rather than as content.
 */
function stripePattern(colour: string, size = 8): ImageData {
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    // A flat block is a poor pattern but a working one; an exception here would
    // take the whole map down over a texture.
    return new ImageData(size, size);
  }
  ctx.strokeStyle = colour;
  ctx.lineWidth = 2;
  // Two strokes offset by the tile size, so the diagonal continues across the
  // seam instead of restarting at every tile.
  ctx.beginPath();
  ctx.moveTo(-size, size);
  ctx.lineTo(size, -size);
  ctx.moveTo(0, size * 2);
  ctx.lineTo(size * 2, 0);
  ctx.stroke();
  return ctx.getImageData(0, 0, size, size);
}

type Mode = "probe" | "march" | "command";
interface Point { lat: number; lon: number }

/** Something the interface said. Shown as a toast since 2026-09-25. */
interface ClientEntry {
  kind: "order" | "refused" | "zone";
  text: string;
}

/** One message in the bottom-left corner, gone after `TOAST_MS`. */
interface Toast {
  id: number;
  kind: string;
  text: string;
}

/** How long a message stays: long enough to read two lines. */
const TOAST_MS = 6_000;
/** A refusal stays longer — it is the one that asks you to do something. */
const TOAST_REFUSED_MS = 9_000;
/** At most this many at once; the oldest goes first. */
const TOASTS_SHOWN = 4;

interface GameProps {
  gameCode: string;
  onLeave: (forget: boolean) => void;
  briefing: boolean;
}

function Game({ gameCode, onLeave, briefing }: GameProps) {
  const container = useRef<HTMLDivElement | null>(null);
  const map = useRef<maplibregl.Map | null>(null);

  // ── ONE MODE ──────────────────────────────────────────────────────────────
  // Probe and March were scaffolding from the phases that proved the terrain and
  // routing chains. They are gone: with a unit menu, a click on the map means an
  // order, and a mode selector that changes what a click means is exactly the
  // kind of hidden state that makes an interface feel unpredictable.
  const mode: Mode = "command";
  const [health, setHealth] = useState<HealthReport | null>(null);
  // ── THERE IS NO SILENT ERROR CHANNEL ANY MORE ─────────────────────────────
  // This was `const [, setError] = useState(...)` — a state nothing rendered.
  // Every failed order was written into it and vanished. Measured 2026-09-10:
  // a move to a point ~2 km from a formation returns 500 from the server, the
  // catch wrote it here, and the map simply did nothing — which is exactly what
  // the owner reported as *"händer ingenting när jag klickar"*.
  //
  // Failures go to the log with everything else now. Keeping a write-only error
  // sink next to a working log was the whole defect.
  const [mapReady, setMapReady] = useState(false);
  const [world, setWorld] = useState<WorldReport | null>(null);
  const [campaign, setCampaign] = useState<CampaignReport | null>(null);
  const [zoc, setZoc] = useState<ZocReport | null>(null);
  /** Bumped to force a zone refetch after something that changes it. */
  const [zocNonce, setZocNonce] = useState(0);
  const [resetting, setResetting] = useState(false);
  // The theatre clock. This was `economy`, holding the stockpile report that
  // carried the game time, pause state and speed along with it — see
  // `fetchClock` for why the three outlived the rest.
  const [clock, setClock] = useState<ClockReport | null>(null);
  const [log, setLog] = useState<LogReport | null>(null);
  const [clockBusy, setClockBusy] = useState(false);
  // Speed is the server's answer, never the button that was pressed — see the
  // rate buttons in the top bar.
  const [rate, setRate] = useState(1);
  const [briefingOpen, setBriefingOpen] = useState(briefing);
  const [settingsOpen, setSettingsOpen] = useState(false);
  // The census panel and the reference page went with `/api/census` and
  // `/api/reference`: one measured what the ground was worth, the other listed
  // what everything cost to build and raise.
  /** Reach settings, seeded from the zone report so the menu opens on what is
   *  actually in force rather than on a client-side guess. */
  const [zocSettings, setZocSettings] = useState<ZocSettings | null>(null);
  // The unit menu. `pending` is the action waiting for a map click — "move" and
  // "build" need a destination, everything else acts where the unit stands.
  const [menuFor, setMenuFor] = useState<number | null>(null);
  const [pending, setPending] = useState<{ action: string; arg?: string } | null>(null);
  const [acting, setActing] = useState(false);
  // ── OUTCOMES GO TO THE LOG, NOT INTO A PANEL ──────────────────────────────
  // Owner, 2026-09-10: *"Meny likt 'Order given' ska tas bort. Denna info ska in
  // i log."*
  //
  // He is right about more than the tidiness. A panel that appears over the map
  // to say "Order given." interrupts the one thing you are looking at to tell
  // you something you just did on purpose — and then it is gone, so the one
  // place that *should* remember it does not. The log already exists and is
  // already the view that makes a night away comprehensible; an order is
  // exactly the sort of thing it should carry.
  //
  // Refusals go the same way. They are information, not errors, and the log is
  // where information about what happened belongs.
  //
  // ── THE LOG IS GONE: MESSAGES COME AND GO ─────────────────────────────────
  // Owner, 2026-09-25: *"Loggen tar upp yta. inför istället att info visas i
  // nedre kan[ten] och sen försvinner."* The panel held eight lines over the
  // bottom-left of the map for good. Every line it carried — orders,
  // refusals, zone warnings and the server's own events — now appears in the
  // same corner as a toast and fades. What goes with it is the backlog: a
  // player back after a night away no longer reads what happened meanwhile.
  // The server still keeps it (`/api/log`).
  const [toasts, setToasts] = useState<Toast[]>([]);
  const toastId = useRef(0);
  const toast = useCallback((kind: string, text: string) => {
    const id = ++toastId.current;
    setToasts((prev) => [...prev, { id, kind, text }].slice(-TOASTS_SHOWN));
    window.setTimeout(
      () => setToasts((prev) => prev.filter((t) => t.id !== id)),
      kind === "refused" ? TOAST_REFUSED_MS : TOAST_MS,
    );
  }, []);
  const say = useCallback(
    (kind: ClientEntry["kind"], ...lines: string[]) => {
      const text = lines.filter((l) => l && l.length > 0).join(" ");
      if (!text) return;
      toast(kind, text);
    },
    [toast],
  );
  const [aerial, setAerial] = useState(false);
  const [aerialInfo, setAerialInfo] = useState<{ attribution: string; licence: string } | null>(null);
  // The IGM 1:100,000 period map (Sicily.md §47, 2026-09-24). Since
  // 2026-09-25 it is part of the 1943 view below rather than its own button.
  // ── THE 1943 VIEW, ON FROM THE START ─────────────────────────────────────
  // Owner, 2026-09-25: *"Kan vi testa att ha 1943 som standard men använda
  // vägnätet från idag och se hur det fungerar?"* and, the same day, *"Undvik
  // IGM och använda enbart AMS så långt det räcker."* Three AMS series,
  // coarsest under finest (`AMS_LAYERS`). Today's roads stay drawn above
  // them was tried and taken off the same day (below); today's place names
  // and roads only show below zoom 8, where the old maps are not drawn.
  const [ams, setAms] = useState(true);
  const [historicInfo, setHistoricInfo] = useState<{ attribution: string; licence: string; minZoom: number } | null>(null);
  const [selected, setSelected] = useState<number | null>(null);
  /** Live zoom, shown in the top bar so a zoom level can be named out loud. */
  const [zoomLevel, setZoomLevel] = useState<number | null>(null);
  const unitMarkers = useRef<Map<number, maplibregl.Marker>>(new Map());
  const arrivalAnimations = useRef<Map<number, ArrivalAnimation>>(new Map());
  const transportMarkers = useRef<Map<number, maplibregl.Marker>>(new Map());
  const transportArrivals = useRef<ArrivalView[]>([]);
  const transportClock = useRef<TransportClock | null>(null);
  const knownUnitIds = useRef<Set<number>>(new Set());
  const animatedArrivalIds = useRef<Set<number>>(new Set());
  const knownWorldId = useRef<number | null>(null);

  // ── SYMBOL SIZE FOLLOWS THE SCREEN ────────────────────────────────────────
  // Owner, 2026-09-09: *"Enheter syns jättebra på telefon … men är för litet på
  // dator."* Regenerated at a larger size rather than scaled with CSS, because
  // the marker's anchor comes from the element's real dimensions — a transform
  // would move the drawing away from the point it marks.
  const [symbolSize, setSymbolSize] = useState(() =>
    symbolSizeFor(typeof window === "undefined" ? 1200 : window.innerWidth),
  );
  useEffect(() => {
    const onResize = () => setSymbolSize(symbolSizeFor(window.innerWidth));
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Mode and origin are read inside the map's click handler, which is
  // registered once. Refs keep it looking at current values instead of the
  // ones captured at mount.
  const modeRef = useRef(mode);
  const selectedRef = useRef(selected);
  modeRef.current = mode;
  selectedRef.current = selected;

  // ── HEAPS OPEN INTO A RING ────────────────────────────────────────────────
  // Owner, 2026-09-11: *"När många enheter samlas när så är det svårt att välja.
  // Skapa en presentera enheter i hög i en ring när man hovrar över dom."*
  //
  // Hovering a formation that shares its spot fans the whole heap out round the
  // heap's centre; leaving the ring folds it back. A phone has no hover — the
  // menu decision of 2026-09-10 was made for exactly that reason — so there the
  // first tap on a heap opens it and the next tap picks.
  //
  // Markers are moved with `setOffset`, never `setLngLat`: every formation stays
  // at its true position, so an order given while the ring is open still aims at
  // real ground, and the next poll cannot fight the ring over where it is.
  const symbolSizeRef = useRef(symbolSize);
  symbolSizeRef.current = symbolSize;
  const spreadRef = useRef<number[] | null>(null);
  const ringMarker = useRef<maplibregl.Marker | null>(null);
  const foldTimer = useRef<number | null>(null);
  const canHover =
    typeof window !== "undefined" && !!window.matchMedia?.("(hover: hover)").matches;

  const cancelFold = () => {
    if (foldTimer.current !== null) {
      window.clearTimeout(foldTimer.current);
      foldTimer.current = null;
    }
  };

  const foldHeap = () => {
    cancelFold();
    for (const id of spreadRef.current ?? []) {
      const mk = unitMarkers.current.get(id);
      if (!mk) continue;
      mk.setOffset([0, 0]);
      mk.getElement().classList.remove("is-spread");
    }
    spreadRef.current = null;
    ringMarker.current?.remove();
    ringMarker.current = null;
  };

  // A short grace period: opening the ring moves the hovered symbol out from
  // under the pointer, and folding on that very mouseleave would snap it shut.
  const foldSoon = () => {
    cancelFold();
    if (spreadRef.current) foldTimer.current = window.setTimeout(foldHeap, 450);
  };

  /** The heap a formation is drawn in, or null when it stands alone. */
  const heapOf = (unitId: number): number[] | null => {
    const m = map.current;
    if (!m) return null;
    const points: ScreenPoint[] = [];
    for (const [id, mk] of unitMarkers.current) {
      const p = m.project(mk.getLngLat());
      points.push({ id, x: p.x, y: p.y });
    }
    // Closer than most of a symbol and the frames cover each other; further
    // apart only the captions touch, which the eye separates without help.
    return findStacks(points, symbolSizeRef.current * 0.9).find((g) => g.includes(unitId)) ?? null;
  };

  const openHeap = (ids: number[]) => {
    const m = map.current;
    if (!m) return;
    foldHeap();
    const size = symbolSizeRef.current;
    const at = ids.map((id) => m.project(unitMarkers.current.get(id)!.getLngLat()));
    const cx = at.reduce((a, p) => a + p.x, 0) / at.length;
    const cy = at.reduce((a, p) => a + p.y, 0) / at.length;
    // Spaced for a symbol *and* its caption, which is the wider of the two.
    const ring = ringOffsets(ids.length, Math.max(size * 2.2, 72));
    const radius = Math.hypot(ring[0].dx, ring[0].dy);
    ids.forEach((id, i) => {
      const mk = unitMarkers.current.get(id)!;
      mk.setOffset([cx + ring[i].dx - at[i].x, cy + ring[i].dy - at[i].y]);
      mk.getElement().classList.add("is-spread");
    });

    const d = Math.round(2 * (radius + size * 1.3));
    const c = d / 2;
    const el = document.createElement("div");
    el.className = "stack-ring";
    el.style.width = `${d}px`;
    el.style.height = `${d}px`;
    el.innerHTML =
      `<svg width="${d}" height="${d}" aria-hidden="true">` +
      ring.map((o) => `<line x1="${c}" y1="${c}" x2="${c + o.dx}" y2="${c + o.dy}" />`).join("") +
      `<circle cx="${c}" cy="${c}" r="4" /></svg>`;
    el.addEventListener("mouseenter", cancelFold);
    el.addEventListener("mouseleave", foldSoon);
    // A click on the ring's empty space closes it rather than reaching the map
    // — unless an order is armed, when it is the destination.
    //
    // Owner, 2026-09-25: *"Jag vill också att du fixar så att man kan välja
    // destination inom en strids blob, som det är nu så går det inte att
    // klicka inom blob."* Formations in a fight stand within firing range of
    // each other, close enough on the map to heap, and hovering one spreads
    // the heap into this ring — a disc of DOM over the middle of the battle
    // area. Its click was stopped here and read as "fold", so a destination
    // clicked there never reached `handleClick`, and nothing said why.
    el.addEventListener("click", (e) => {
      e.stopPropagation();
      if (airArmedRef.current || (pendingRef.current && selectedRef.current !== null)) {
        const box = m.getCanvas().getBoundingClientRect();
        const at = m.unproject([e.clientX - box.left, e.clientY - box.top]);
        foldHeap();
        handleClickRef.current?.({ lat: at.lat, lon: at.lng }, m.getZoom());
        return;
      }
      foldHeap();
    });
    ringMarker.current = new maplibregl.Marker({ element: el, anchor: "center" })
      .setLngLat(m.unproject([cx, cy]))
      .addTo(m);
    spreadRef.current = ids;
  };



  /** Roads and cross-country legs are drawn as separate sources so they can be
   *  styled differently — a march over open ground should not look like a road. */


  /** Redraw unit markers. Markers are reused by id so they animate smoothly
   *  as positions update rather than flickering on every poll. */
  /**
   * The tooltip, by grade of identification.
   *
   * ── IT USED TO SAY THE SAME THING TWICE ───────────────────────────────────
   * The line was `${unit.name} — ${formationLabel(branch, echelon)}` for every
   * formation. That reads correctly for your own — a name and then what it is —
   * but the server redacts an unidentified enemy by setting its *name* to
   * `"{branch} {echelon}"`. Measured: a hostile rifle company showed
   * **"infantry company — Rifle company"**, the same fact raw and then
   * prettified. The duplication arrived with redaction on 2026-09-12 and was
   * only visible once the grades made it worth looking at.
   *
   * Owner, 2026-09-17: unit information shows when the enemy is identified. So
   * a name is only worth printing when there is a real one to print.
   */
  const titleFor = useCallback(
    (unit: Unit) => {
      const activity = unit.withdrawing
        ? (unit.starts_in_s ?? 0) > 0 ? "disengaging" : "retreating"
        : unit.engaged
          ? "fighting"
        : unit.progress !== null
          ? (unit.starts_in_s ?? 0) > 0 ? "disengaging" : "moving"
          : "holding";
      return unit.mine || unit.identified
        ? `${unit.name} — ${formationLabel(unit.branch, unit.echelon)} — ${activity}`
        : formationLabel(unit.branch, unit.echelon);
    },
    [],
  );

  const renderUnits = useCallback(
    (worldId: number, units: Unit[], selectedId: number | null, size: number) => {
    const m = map.current;
    if (!m) return;

    // A continued campaign starts from what is already there; it does not
    // replay yesterday's landings. A firing instant is the exception: if the
    // map became ready a moment after the event, that fresh entry still gets
    // the journey the player was waiting to see.
    if (knownWorldId.current !== worldId) {
      knownWorldId.current = worldId;
      knownUnitIds.current = new Set(units.map((u) => u.id));
      animatedArrivalIds.current.clear();
      arrivalAnimations.current.clear();
    }

    const seen = new Set<number>();
    for (const unit of units) {
      seen.add(unit.id);
      const wasKnown = knownUnitIds.current.has(unit.id);
      knownUnitIds.current.add(unit.id);
      let marker = unitMarkers.current.get(unit.id);
      // Every badge decided once, for the symbol and the hover card alike.
      const status = statusOf(unit);
      const html = symbolFromSidc(unit.sidc, {
        // Owner, 2026-09-18: echelon above the symbol, the formation's short
        // name below. An unidentified enemy keeps the generic type that sight
        // earns; using its redacted `name` would print the same fact less well.
        label:
          unit.mine || unit.identified
            ? unit.name
            : formationLabel(unit.branch, unit.echelon),
        // Owner, 2026-09-11: his formations could not be told from the enemy's.
        affiliation: unit.mine ? "friendly" : "hostile",
        engaged: unit.engaged,
        // Owner, 2026-09-17: a formation resting in supply says so on the map.
        // The rule lives in `isResting`, which mirrors the server's `recover`.
        // Own formations only since 2026-09-25 (`statusOf`): an identified
        // enemy's progress is withheld, so a marching one read as resting.
        resting: status.resting,
        // Owner, 2026-09-17: national colours. Null for an enemy that is seen
        // but not identified, which draws the old hostile fill instead.
        nation: unit.nation,
        selected: unit.id === selectedId,
        // Owner, 2026-09-25: badges for marching, retreating and holding, the
        // stance, and low fuel and ammunition. `movementOf` draws nothing for
        // an enemy, whose progress is withheld; a redacted enemy's stance is
        // "unknown" and its loads 1.0, which draw nothing either.
        movement: status.movement,
        stance: status.stance,
        lowFuel: status.lowFuel,
        lowAmmo: status.lowAmmo,
        strength: unit.strength,
        size,
      });
      // Sea transport is visible before its event. Replaying it on the newly
      // inserted ground formation would move the landing after its booked time.
      const mode: ArrivalMode | null = unit.arrival_mode === "air" ? "air" : null;
      const justFired =
        unit.arrival_time_ms !== null &&
        Date.now() - unit.arrival_time_ms >= 0 &&
        Date.now() - unit.arrival_time_ms < 12_000;
      const reducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
      const animateArrival =
        mode !== null &&
        !reducedMotion &&
        !animatedArrivalIds.current.has(unit.id) &&
        (unit.mine ? !wasKnown || justFired : justFired);
      // The 2026-09-25 badges are in the key too. A key that leaves one out
      // keeps the old drawing when it changes — strength and resting were both
      // missed here once.
      const badgeKey = `${status.movement ?? ""}-${status.stance}-${status.lowFuel}-${status.lowAmmo}`;
      const stateKey = `${unit.id === selectedId}-${unit.progress !== null}-${Math.round(
        unit.strength * 20,
      )}-${unit.engaged}-${status.resting}-${unit.identified}-${unit.nation ?? ""}-${size}-${badgeKey}`;

      if (!marker) {
        const el = document.createElement("div");
        el.className = "unit-marker";
        el.innerHTML = animateArrival && mode ? arrivalMarkup(mode, html) : html;
        el.dataset.state = stateKey;
        el.title = titleFor(unit);
        el.addEventListener("mouseenter", () => {
          const m2 = map.current;
          if (!m2) return;
          cancelFold();
          if (canHover && !spreadRef.current?.includes(unit.id)) {
            const heap = heapOf(unit.id);
            if (heap) openHeap(heap);
          }
          // The hover card follows the symbol, which may just have moved out
          // onto the ring.
          const mk = unitMarkers.current.get(unit.id);
          const p = m2.project(mk ? mk.getLngLat() : [unit.lon, unit.lat]);
          const o = mk?.getOffset();
          setHover({ id: unit.id, x: p.x + (o?.x ?? 0), y: p.y + (o?.y ?? 0) });
        });
        el.addEventListener("mouseleave", () => {
          setHover((h) => (h?.id === unit.id ? null : h));
          foldSoon();
        });
        el.addEventListener("click", (e) => {
          // ── A MARKER MUST NOT EAT AN ARMED ORDER ──────────────────────────
          // Symbols are ~40 px with a caption under them, so they cover real
          // ground. With an order armed, a click on one used to be swallowed
          // here and re-read as "select this other unit" — the order stayed
          // armed, nothing moved, and no reason was given. Sending a formation
          // to where another one stands is an ordinary thing to want.
          if (airArmedRef.current) {
            e.stopPropagation();
            if (unit.mine) {
              say("refused", "Pick an enemy formation for the air strike.");
            } else {
              strikeRef.current?.(unit.id, unit.name);
            }
            return;
          }
          if (pendingRef.current && selectedRef.current !== null) {
            e.stopPropagation();
            handleClickRef.current?.({ lat: unit.lat, lon: unit.lon }, 0);
            return;
          }
          // Otherwise: stop the map handler firing too, or selecting a unit
          // would also count as ordering it somewhere.
          e.stopPropagation();
          // No hover on a phone: the first tap on a heap opens it, the next
          // tap on one of its formations picks that one.
          if (!canHover && !spreadRef.current?.includes(unit.id)) {
            const heap = heapOf(unit.id);
            if (heap) {
              openHeap(heap);
              return;
            }
          }
          // The menu opens at the formation's true position, so the ring folds
          // first and the chosen symbol goes back to where the menu will be.
          foldHeap();
          // Selecting and opening the menu are the same gesture. A separate
          // right-click or long-press would be invisible on a phone, which is
          // where this is actually being looked at.
          setSelected(unit.id);
          // Owner, 2026-09-11: the menu is for his own formations only. An enemy
          // can still be selected — that is how its firing range is read.
          setMenuFor((cur) => (unit.mine && cur !== unit.id ? unit.id : null));
          // Selecting no longer clears anything: the log keeps its own record.
        });
        const firstFrame = mode && animateArrival
          ? arrivalFrame(mode, unit.lat, unit.lon, 0)
          : { lat: unit.lat, lon: unit.lon };
        marker = new maplibregl.Marker({ element: el, anchor: "center" })
          .setLngLat([firstFrame.lon, firstFrame.lat])
          .addTo(m);
        unitMarkers.current.set(unit.id, marker);
        if (mode && animateArrival) {
          animatedArrivalIds.current.add(unit.id);
          el.classList.add("is-arriving", `is-arriving-${mode}`);
          arrivalAnimations.current.set(unit.id, {
            mode,
            startedAt: performance.now(),
            target: { lat: unit.lat, lon: unit.lon },
            finalHtml: html,
            finalState: stateKey,
          });
        }
      } else {
        const el = marker.getElement();
        const arrival = arrivalAnimations.current.get(unit.id);
        if (arrival) {
          // A poll or resize may redraw the NATO symbol during the journey.
          // Keep the temporary vehicle intact and apply the newest drawing at
          // touchdown instead of restarting the animation.
          arrival.target = { lat: unit.lat, lon: unit.lon };
          arrival.finalHtml = html;
          arrival.finalState = stateKey;
        } else {
          marker.setLngLat([unit.lon, unit.lat]);
        }
        // The key has to name everything the drawing depends on. It listed
        // selection and movement only, so a formation that took losses — or a
        // window that was resized — kept the symbol it was first drawn with.
        // 🔴 The resting state belongs in here too. Without it, a formation that
        // stops and begins recovering keeps the symbol it was last drawn with —
        // the badge would pass every test and never appear on the map.
        // 🔴 And the grade belongs in here for the third time in the same
        // place. Strength was missing, then resting; identification is the same
        // fault again — a formation that becomes identified would keep the
        // symbol and the caption it was drawn with while unidentified, and the
        // rule would pass every test without ever reaching the map.
        if (!arrival && el.dataset.state !== stateKey) {
          el.innerHTML = html;
          el.dataset.state = stateKey;
        }
        // Set here as well, not only where the marker is born: the title used to
        // be written once and never again, so a formation that became
        // identified kept the tooltip it was created with.
        el.title = titleFor(unit);
      }
      marker.getElement().classList.toggle("is-selected", unit.id === selectedId);
    }

    for (const [id, marker] of unitMarkers.current) {
      if (!seen.has(id)) {
        if (spreadRef.current?.includes(id)) foldHeap();
        marker.remove();
        unitMarkers.current.delete(id);
        arrivalAnimations.current.delete(id);
      }
    }

    // Draw the marching orders of every unit currently under way.
    const source = m.getSource("orders") as maplibregl.GeoJSONSource | undefined;
    source?.setData({
      type: "FeatureCollection",
      features: units
        .filter((u) => u.path && u.path.length > 1)
        .map((u) => ({
          type: "Feature",
          properties: { selected: u.id === selectedId },
          geometry: { type: "LineString", coordinates: u.path as [number, number][] },
        })),
    });
  }, []);

  // ── THE BUILDING LAYER WENT WITH THE BUILDINGS ───────────────────────────
  // It drew standing structures with a solid pentagon and sites with the
  // standard's dashed *planned* frame and a countdown. Two details are worth
  // keeping for anything block D draws on the map:
  //
  // · The countdown used the theatre clock **that came with the same report**,
  //   so a marker could not disagree with itself.
  // · Strength was part of the marker's cache key. Without it a structure that
  //   took damage kept the markup it was first drawn with and never updated —
  //   which looks exactly like the damage rule not working.

  // 🔴 `commandMove` below was deleted by accident while the building layer was
  // being removed — it sat in the same block — and restored verbatim from git.
  // It is ordinary movement code and nothing to do with buildings.
  const commandMove = useCallback(async (unitId: number, to: Point, mode?: MoveMode) => {
    try {
      const order = await orderMove(unitId, to.lat, to.lon, mode);
      // ── SAY WHEN A ROUTE IS ONLY AN ESTIMATE ──────────────────────────────
      // `overland` is a straight line at cross-country pace with no terrain
      // read — it does not check water. It exists so a march that is obviously
      // possible is never refused, and this line exists so it is never mistaken
      // for a planned route. Owner, 2026-09-10: *"det behöver inte enbart vara
      // på väg men snabbaste vägen dit ska beräknas."*
      if (order.mode === "overland") {
        say(
          "order",
          `${Math.round(order.distance_m / 1000)} km overland — too far to plan a road, ` +
            `straight-line estimate that does not check water.`,
        );
      } else if (order.mode === "cross_country") {
        say("order", `${Math.round(order.distance_m / 1000)} km across country.`);
      }
      const w = await fetchWorld();
      setWorld(w);
      setZocNonce((n) => n + 1);
    } catch (e) {
      say("refused", e instanceof Error ? e.message : "Order failed");
    }
  }, []);

  // A click on the map only ever means one thing: carry out whatever the unit
  // menu armed. With the mode selector gone there is no hidden state deciding
  // what a click does — either something is pending or the click is idle.
  const pendingRef = useRef<{ action: string; arg?: string } | null>(null);
  const airArmedRef = useRef(false);
  const strikeRef = useRef<((targetId: number, name: string) => void) | null>(null);
  pendingRef.current = pending;
  /** Markers are created once and outlive any single render, so they reach the
   *  click handler through a ref rather than closing over it. */
  const handleClickRef = useRef<((p: Point, zoom: number) => void) | null>(null);

  // ── A CLICK THAT DOES NOTHING MUST SAY WHY ────────────────────────────────
  // Owner, 2026-09-10: *"Många gånger när jag väljer move så händer ingenting
  // när jag klickar. Varför?"*
  //
  // The honest answer was that this handler returned silently, so there was no
  // way to tell which of several causes it was — and no way for me to reproduce
  // it from here. Every one of them now leaves a line in the log, so the next
  // occurrence diagnoses itself instead of being guessed at. That is the same
  // discipline as the zone bugs: measure it where it happens.
  const handleClick = useCallback(
    (p: Point, _zoom: number) => {
      const armed = pendingRef.current;
      const unit = selectedRef.current;
      if (airArmedRef.current) {
        setAirArmed(false);
        say("order", "Air strike called off — click an enemy formation to strike it.");
        return;
      }
      if (!armed) {
        // ── A CLICK ELSEWHERE CLOSES THE MENU ─────────────────────────────
        // Owner, 2026-09-10: *"När jag klickar på något annat - tex karta -
        // ska öppen enhetsmeny stängas."*
        //
        // The menu used to persist until its × was pressed, which put a panel
        // over the map that outlived any interest in it. Closing on an
        // unrelated click is what every other menu on the platform does.
        //
        // No log line for this: dismissing a menu is not an outcome, and the
        // "no order armed" note that used to be written here fired on every
        // stray click and drowned the log it was meant to inform.
        setMenuFor(null);
        setSettingsOpen(false);
        return;
      }
      if (unit === null) {
        say("refused", "Nothing is selected any more — the order was dropped.");
        setPending(null);
        return;
      }

      setPending(null);
      if (armed.action === "move") {
        // The armed action carries the choice, the same way a build order
        // carries which building. Absent means "whichever is quicker".
        void commandMove(unit, p, armed.arg as MoveMode | undefined);
        say("order", "Order given.");
        return;
      }
      // The `build` branch went with `/api/build`. It carried the unit with the
      // order on purpose — the server decides whether that formation may start
      // the work, and it cannot decide without it. Worth copying for any order
      // block D adds that a particular formation has to give.
    },
    [commandMove],
  );

  handleClickRef.current = handleClick;

  // ── MAP INITIALISATION ────────────────────────────────────────────────────
  // Rebuilt after the same bad edit. The layer order below is the design and not
  // an accident: imagery sits on the map for orientation, the zones above it as
  // context, routes and orders above those, and the unit markers — which are DOM
  // elements, not layers — on top of everything.
  useEffect(() => {
    if (!container.current || map.current) return;
    let cancelled = false;

    void (async () => {
      // The tile URL comes from the server so the provider is named in one file.
      let style;
      try {
        const cfg = await fetchBasemaps();
        const first = cfg.sources[0];
        style = buildStyle(first.tile_url, first.attribution);
      } catch {
        // A blank style still shows units and zones. Refusing to start because a
        // basemap is unreachable would hide a working game behind a missing one.
        style = buildBlankStyle();
      }
      if (cancelled || !container.current) return;

      const instance = new maplibregl.Map({
        container: container.current,
        style,
        center: [INITIAL_VIEW.lng, INITIAL_VIEW.lat],
        zoom: INITIAL_VIEW.zoom,
        attributionControl: false,
        // MapLibre calls a press a *drag* — and fires no `click` at all — once
        // the pointer moves past this many pixels. The default of 3 is tight
        // enough that a trackpad press on a large screen regularly overshoots
        // it, which is one of the ways an order can be given and nothing
        // happens. 6 still reads a real pan as a pan.
        clickTolerance: 6,
      });

      instance.on("load", () => {
        // ── HOW FAR OUT "THE WHOLE ISLAND" IS ─────────────────────────────
        // Owner, same day: *"det ska inte gå att zooma ut längre än hela
        // sicilien."*
        //
        // Computed, not a constant: the zoom that fits Sicily on a phone is not
        // the one that fits it on a desktop, and a hardcoded floor would either
        // crop the north coast or leave a wide margin of sea. The map opens at
        // that same zoom, so the campaign starts showing the whole island.
        // ── ZOOM OUT SHOWS THE WHOLE ISLAND, ZOOM IN STOPS AT z10 ─────────
        // Owner, 2026-09-15: *"man kan zooma in till z10 och zooma ut så att
        // hela Sicilien syns. men rutter beräknas på z11."*
        //
        // The floor is computed from the viewport, because the zoom that fits
        // Sicily on a phone is not the one that fits it on a desktop. A fixed
        // floor was tried for one commit, on a misreading of his instruction,
        // and it took the whole-island view away.
        const floor = minZoomForBounds(
          SICILY_BOUNDS,
          instance.getContainer().clientWidth,
          instance.getContainer().clientHeight,
        );
        instance.setMinZoom(floor);
        // The ceiling is the zoom the road graph was built at — past it the
        // basemap would draw roads the simulation has never heard of.
        instance.setMaxZoom(MAX_ZOOM);
        instance.jumpTo({ center: [INITIAL_LNG, INITIAL_LAT], zoom: floor });
        setZoomLevel(floor);

        // ── THE CENTRE IS FENCED, NOT THE VIEWPORT ────────────────────────
        // Owner, 2026-09-15: *"man ska inte kunna panorera bort från
        // sicilien."*
        //
        // `maxBounds` was the obvious tool and it is the wrong one: it requires
        // the viewport to fit *inside* the box, which on a screen not shaped
        // like Sicily contradicts showing the whole island — MapLibre pulls the
        // zoom back in and crops the coasts. Clamping the centre satisfies both
        // halves of the order: the island always fits, and it can never slide
        // off the edge.
        //
        // The clamp converges rather than loops: setCenter fires `move` again,
        // the centre is inside by then, and the equality check stops it.
        instance.on("move", () => {
          const c = instance.getCenter();
          const [lng, lat] = clampCentre(CAMERA_BOUNDS, c.lng, c.lat);
          if (lng !== c.lng || lat !== c.lat) instance.setCenter([lng, lat]);
          setZoomLevel(instance.getZoom());
        });

        // Satellite imagery, off until asked for. A photograph is orientation,
        // not the playing surface, so everything that matters draws over it.
        //
        // 🔴 That was the intent before too, and the code did the opposite.
        // Owner, 2026-09-15: *"kan du även lägga till vägar på flygfoto
        // kartan?"* — `addLayer` without a `beforeId` appends to the **top** of
        // the stack, not the bottom. Insertion order is not stack position. So
        // the photo, at 0.9 opacity, painted straight over the roads, water and
        // labels that `buildStyle` had already put there. Units and zones
        // survived only because they are added *after* it and landed higher
        // still, which is what made the fault look like "roads are missing"
        // rather than "the photo covers the map".
        //
        // Anchored under the first road layer: the photo replaces the drawn
        // ground, and roads, rail, boundaries and place names stay legible on
        // top of it.
        instance.addSource("aerial", {
          type: "raster",
          tiles: [],
          tileSize: 256,
          maxzoom: 14,
        });
        // The blank fallback style has only `background`, so the anchor has to
        // be checked rather than assumed — naming a layer that does not exist
        // throws, and would take the whole map down with the basemap chain.
        const beneathRoads = instance.getLayer("roads-minor") ? "roads-minor" : undefined;
        instance.addLayer(
          {
            id: "aerial-layer",
            type: "raster",
            source: "aerial",
            layout: { visibility: "none" },
            paint: { "raster-opacity": 0.9 },
          },
          beneathRoads,
        );

        // Zone of control: your ground, theirs, and the overlap. Three sources
        // so they can be styled apart; the hatched middle is the only place you
        // may not build or repair.
        instance.addSource("zoc", {
          type: "geojson",
          data: { type: "FeatureCollection", features: [] },
        });
        instance.addLayer({
          id: "zoc-fill",
          type: "fill",
          source: "zoc",
          paint: { "fill-color": "#4d94e0", "fill-opacity": 0.14 },
        });

        // ── GROUND WITH NO HEADQUARTERS IN IT ─────────────────────────────
        // Owner, 2026-09-10: *"Alla separerade blobbar (mina) som inte har
        // kontakt med ett HQ ska ha blå randig bakgrund för att visa att dessa
        // är skilda från main blob."*
        //
        // Hatching rather than a second flat colour, because this is the same
        // side's ground in a different *state* — not a different owner. The
        // contested overlap already uses that language, so a formation reading
        // the map sees one visual grammar: solid is held, hatched is qualified.
        //
        // ⚠️ Amended 2026-09-18: Husky supply now follows roads from beaches and
        // ports, with hostile ZoC closing the road. The server therefore sends
        // this source empty rather than drawing the old HQ-polygon split as if
        // it still explained supply. The layer stays ready for a future drawing
        // of the actual detached road corridors.
        instance.addImage("hatch-blue", stripePattern("#4d94e0"), { pixelRatio: 2 });
        instance.addSource("zoc-detached", {
          type: "geojson",
          data: { type: "FeatureCollection", features: [] },
        });
        // ── SAME GROUND, SAID AT THE EDGE ─────────────────────────────────
        // Owner, 2026-09-10: *"Det 'streckade' området som inte ingår i ZOC med
        // HQ syns dåligt. Använd samma färg som nationen men med en avbrytande,
        // tydlig färg på ZOC-kanten."*
        //
        // The hatch was doing the work and doing it badly: a pattern at 55%
        // over a dark map is texture you have to look for. His instruction is
        // better design than mine was — the *fill* says whose ground it is and
        // must therefore match the nation, and the *edge* says what is wrong
        // with it. One channel per fact instead of one channel carrying both.
        //
        // So the fill is now the nation's own blue at the same opacity as the
        // connected zone, and the boundary is amber: the colour already used
        // for contested ground and the movement badge, i.e. the map's existing
        // word for "this needs your attention".
        instance.addLayer({
          id: "zoc-detached-fill",
          type: "fill",
          source: "zoc-detached",
          paint: { "fill-color": "#4d94e0", "fill-opacity": 0.14 },
        });
        instance.addLayer({
          id: "zoc-detached-outline",
          type: "line",
          source: "zoc-detached",
          layout: { "line-cap": "butt", "line-join": "round" },
          paint: {
            "line-color": "#e8c15a",
            // Thicker than the ordinary zone edge (1.6) and dashed short, so it
            // reads as a cut rather than as a border. "Avbrytande" is the word
            // he used and the dash is what carries it.
            "line-width": 3,
            "line-opacity": 0.95,
            "line-dasharray": [2, 1.5],
          },
        });
        // A dark under-stroke, so the amber holds its own over both the dark
        // basemap and satellite imagery. Added first would put it above the
        // line it backs; MapLibre draws in insertion order, so it goes before.
        instance.addLayer(
          {
            id: "zoc-detached-outline-under",
            type: "line",
            source: "zoc-detached",
            paint: { "line-color": "#0b1016", "line-width": 5, "line-opacity": 0.7 },
          },
          "zoc-detached-outline",
        );

        instance.addSource("zoc-enemy", {
          type: "geojson",
          data: { type: "FeatureCollection", features: [] },
        });
        instance.addLayer({
          id: "zoc-enemy-fill",
          type: "fill",
          source: "zoc-enemy",
          paint: { "fill-color": "#e0685d", "fill-opacity": 0.13 },
        });
        instance.addLayer({
          id: "zoc-enemy-outline",
          type: "line",
          source: "zoc-enemy",
          paint: { "line-color": "#e0685d", "line-width": 1.4, "line-opacity": 0.8 },
        });

        instance.addSource("zoc-contested", {
          type: "geojson",
          data: { type: "FeatureCollection", features: [] },
        });
        instance.addLayer({
          id: "zoc-contested-fill",
          type: "fill",
          source: "zoc-contested",
          paint: { "fill-color": "#d6a24a", "fill-opacity": 0.26 },
        });
        instance.addLayer({
          id: "zoc-contested-outline",
          type: "line",
          source: "zoc-contested",
          paint: { "line-color": "#e8bd6b", "line-width": 1.8, "line-dasharray": [3, 2] },
        });

        instance.addLayer({
          id: "zoc-outline",
          type: "line",
          source: "zoc",
          layout: { "line-cap": "round", "line-join": "round" },
          paint: { "line-color": "#6fb0ee", "line-width": 1.6, "line-opacity": 0.85 },
        });

        // ── WHERE YOU MAY NOT BUILD ──────────────────────────────────────
        // Hidden until a build is armed. A permanent shading of the whole map
        // would be answering a question nobody asked, on every screen, forever.
        instance.addSource("nobuild", {
          type: "geojson",
          data: { type: "FeatureCollection", features: [] },
        });
        instance.addLayer({
          id: "nobuild-fill",
          type: "fill",
          source: "nobuild",
          layout: { visibility: "none" },
          paint: { "fill-color": "#0b1016", "fill-opacity": 0.5 },
        });

        instance.addSource("route", {
          type: "geojson",
          data: { type: "FeatureCollection", features: [] },
        });
        instance.addLayer({
          id: "route-line",
          type: "line",
          source: "route",
          layout: { "line-cap": "round", "line-join": "round" },
          paint: { "line-color": "#8fc4f5", "line-width": 3 },
        });

        instance.addSource("offroad", {
          type: "geojson",
          data: { type: "FeatureCollection", features: [] },
        });
        instance.addLayer({
          id: "offroad-line",
          type: "line",
          source: "offroad",
          layout: { "line-cap": "round", "line-join": "round" },
          paint: {
            "line-color": "#d6a24a",
            "line-width": 2.5,
            "line-dasharray": [2, 2],
          },
        });

        // ── OBJECTIVES ────────────────────────────────────────────────
        // Drawn under the formation symbols and above the zone, because a town
        // has to stay readable while a column is standing on it — which is
        // exactly the moment a player looks at it.
        //
        // Colour carries ownership and nothing else: yours, theirs, nobody's.
        // The radius is the capture radius, so what the map shows is the rule
        // rather than a decoration around it.
        instance.addSource("objectives", {
          type: "geojson",
          data: { type: "FeatureCollection", features: [] },
        });
        instance.addLayer({
          id: "objective-ring",
          type: "circle",
          source: "objectives",
          paint: {
            "circle-radius": ["interpolate", ["linear"], ["zoom"], 6, 4, 10, 9, 14, 16],
            "circle-color": [
              "case",
              ["get", "mine"],
              "#4d9be6",
              ["get", "enemy"],
              "#d4695f",
              "#8a8f98",
            ],
            // ── A KEY POSITION IS FILLED, A MARKER IS A HINT ────────────────
            // Owner, 2026-09-17: *"Key point är svåra att skilja från vanliga
            // punkter. Kan vi ha ifylld inre cirkel med en yttre cirkel på Key
            // points?"*
            //
            // The second ring alone put the difference on the *outside* of the
            // symbol, where it competes with everything else drawn near a town.
            // Filling the disc moves it to the middle, where nothing else is,
            // and the two readings reinforce rather than split: solid centre
            // plus outer ring for the 41, a faint disc for the 70.
            //
            // Still the ownership colour in both cases — what changes is weight,
            // not hue, so "whose is it" and "does it matter" stay separate
            // questions with separate channels.
            "circle-opacity": ["case", ["get", "key"], 0.85, 0.22],
            "circle-stroke-width": 2,
            "circle-stroke-color": [
              "case",
              ["get", "mine"],
              "#7fc0ff",
              ["get", "enemy"],
              "#ff9a8f",
              "#c2c8d2",
            ],
          },
        });
        // ── KEY POSITIONS GET A SECOND RING, NOT A COLOUR ───────────────
        // Owner, 2026-09-17: *"Jag ser ingen skillnad mellan vanliga punkter och
        // key points. Skulle vi ha dubbelring eller guldkant?"*
        //
        // A gold edge was the other option and it is the one to avoid: amber is
        // already `STRENGTH_HURT` on a formation and the colour of contested
        // ground. A ring in that colour would read as a *state* — degraded,
        // disputed — rather than as importance, and this codebase has already
        // paid for a badge accidentally drawn in exactly `STRENGTH_HURT`.
        //
        // Colour on this source carries ownership and nothing else, deliberately
        // (see above). A concentric ring is a **shape** difference instead: it
        // survives colour blindness, survives satellite imagery underneath, and
        // costs nothing from a palette that is already crowded.
        //
        // Stroke only — `circle-opacity: 0` — so the ownership fill underneath
        // stays the thing that says whose it is. Drawn between the ring and the
        // label so the name still sits on top.
        instance.addLayer({
          id: "objective-key-ring",
          type: "circle",
          source: "objectives",
          filter: ["==", ["get", "key"], true],
          paint: {
            // 1.75× the base ring at every zoom, so the gap reads as deliberate
            // rather than as two layers that drifted apart.
            "circle-radius": ["interpolate", ["linear"], ["zoom"], 6, 7, 10, 16, 14, 28],
            "circle-opacity": 0,
            "circle-stroke-width": 1.5,
            "circle-stroke-color": [
              "case",
              ["get", "mine"],
              "#7fc0ff",
              ["get", "enemy"],
              "#ff9a8f",
              "#c2c8d2",
            ],
            "circle-stroke-opacity": 0.8,
          },
        });
        instance.addLayer({
          id: "objective-label",
          type: "symbol",
          source: "objectives",
          layout: {
            "text-field": ["get", "label"],
            // ── 🔴 NAME THE FONT. NOT OPTIONAL. ──────────────────────────
            // Omitting `text-font` makes MapLibre fall back to
            // ["Open Sans Regular", "Arial Unicode MS Regular"], which
            // OpenFreeMap's glyph endpoint does not serve. The fetch fails
            // inside the tile worker and **the whole source stops producing
            // features** — so the circle layer above went blank too, with
            // nothing in the console to say why.
            //
            // Measured 2026-09-10 in headless Chrome against dev: the same 24
            // features on a source with only the circle layer gave 51 features
            // and 12 drawn; add this symbol layer without a font and both fell
            // to zero. The source's tile cache filled with 18 empty tiles.
            //
            // "Noto Sans Regular" is what the basemap's own `place-labels`
            // layer asks for, so it is known to exist in this glyph stack.
            "text-font": ["Noto Sans Regular"],
            "text-size": ["interpolate", ["linear"], ["zoom"], 7, 10, 12, 13],
            "text-offset": [0, 1.4],
            "text-anchor": "top",
            "text-allow-overlap": false,
          },
          paint: {
            "text-color": "#e8ecf2",
            "text-halo-color": "#11151c",
            "text-halo-width": 1.6,
          },
        });

        // ── THE HOVERED PHASE ─────────────────────────────────────────
        // Owner, 2026-09-22: phase objectives and lines *"tydligt visas på
        // kartan när spelare hovrar över fas-info"*. Gold, which nothing else
        // on this map uses for ground: ownership keeps blue/red/grey and
        // contested keeps amber, so the highlight cannot be read as a state.
        instance.addSource("phase-highlight", {
          type: "geojson",
          data: { type: "FeatureCollection", features: [] },
        });
        instance.addLayer({
          id: "phase-line",
          type: "line",
          source: "phase-highlight",
          filter: ["==", ["get", "kind"], "line"],
          layout: { "line-cap": "round", "line-join": "round" },
          paint: { "line-color": "#f2c14e", "line-width": 4, "line-opacity": 0.9, "line-dasharray": [2, 1] },
        });
        instance.addLayer({
          id: "phase-objective",
          type: "circle",
          source: "phase-highlight",
          filter: ["==", ["get", "kind"], "objective"],
          paint: {
            "circle-radius": ["interpolate", ["linear"], ["zoom"], 6, 11, 10, 22, 14, 34],
            "circle-opacity": 0.12,
            "circle-color": "#f2c14e",
            "circle-stroke-width": 3,
            "circle-stroke-color": ["case", ["get", "mine"], "#7fc0ff", "#f2c14e"],
          },
        });
        instance.addLayer({
          id: "phase-objective-label",
          type: "symbol",
          source: "phase-highlight",
          filter: ["==", ["get", "kind"], "objective"],
          layout: {
            "text-field": ["get", "label"],
            "text-font": ["Noto Sans Regular"],
            "text-size": 14,
            "text-offset": [0, -1.8],
            "text-anchor": "bottom",
            "text-allow-overlap": true,
          },
          paint: { "text-color": "#f2c14e", "text-halo-color": "#11151c", "text-halo-width": 2 },
        });

        instance.addSource("orders", {
          type: "geojson",
          data: { type: "FeatureCollection", features: [] },
        });
        instance.addLayer({
          id: "orders-line",
          type: "line",
          source: "orders",
          layout: { "line-cap": "round", "line-join": "round" },
          paint: {
            "line-color": ["case", ["get", "selected"], "#8fc4f5", "#3d6d99"],
            "line-width": ["case", ["get", "selected"], 3, 2],
            "line-opacity": 0.9,
            "line-dasharray": [3, 2],
          },
        });

        // ── FIRING RANGE OF THE SELECTED FORMATION ─────────────────────────
        // Owner, 2026-09-11. Under the symbols and over the ground: a faint fill
        // so the covered area reads, and a breathing edge so it is never taken
        // for a zone boundary, which is also a line.
        instance.addSource("range", {
          type: "geojson",
          data: { type: "FeatureCollection", features: [] },
        });
        instance.addLayer({
          id: "range-fill",
          type: "fill",
          source: "range",
          paint: {
            "fill-color": ["case", ["get", "mine"], "#80e0ff", "#ff8080"],
            "fill-opacity": 0.08,
          },
        });
        instance.addLayer({
          id: "range-line",
          type: "line",
          source: "range",
          layout: { "line-join": "round" },
          paint: {
            "line-color": ["case", ["get", "mine"], "#8fc4f5", "#ff8080"],
            "line-width": 2,
            "line-opacity": 0.6,
          },
        });
        instance.addSource("support-range", {
          type: "geojson",
          data: { type: "FeatureCollection", features: [] },
        });
        instance.addLayer({
          id: "support-range-line",
          type: "line",
          source: "support-range",
          layout: { "line-join": "round" },
          paint: {
            "line-color": ["case", ["get", "mine"], "#8fc4f5", "#ff8080"],
            "line-width": 1.5,
            "line-opacity": 0.55,
            "line-dasharray": [3, 3],
          },
        });

        // ── FIGHTS ─────────────────────────────────────────────────────────
        // Owner, 2026-09-11: combat is shown on the map, not only in the log.
        // ── BLOWN BRIDGES (Sicily.md §46, 2026-09-24) ─────────────────────
        // A route that bends round a gap the map does not show reads as a
        // broken planner, so the gap is drawn.
        instance.addSource("demolitions", {
          type: "geojson",
          data: { type: "FeatureCollection", features: [] },
        });
        instance.addLayer({
          id: "demolitions",
          type: "circle",
          source: "demolitions",
          paint: {
            "circle-radius": ["interpolate", ["linear"], ["zoom"], 6, 3, 12, 7],
            "circle-color": "#1b1b1b",
            "circle-stroke-color": "#e0443e",
            "circle-stroke-width": 2.5,
          },
        });

        instance.addSource("engagements", {
          type: "geojson",
          data: { type: "FeatureCollection", features: [] },
        });
        instance.addLayer({
          id: "engagements-fill",
          type: "fill",
          source: "engagements",
          paint: { "fill-color": "#ff9d3d", "fill-opacity": 0.08 },
        });
        instance.addLayer({
          id: "engagements-outline",
          type: "line",
          source: "engagements",
          layout: { "line-cap": "round", "line-join": "round" },
          paint: { "line-color": "#ff9d3d", "line-width": 2.5, "line-opacity": 0.95, "line-dasharray": [2, 1.5] },
        });
        instance.on("mouseenter", "engagements-fill", (event) => {
          instance.getCanvas().style.cursor = "pointer";
          const id = Number(event.features?.[0]?.properties?.battle_id);
          if (Number.isFinite(id)) setBattleAreaId(id);
        });
        instance.on("mouseleave", "engagements-fill", () => {
          instance.getCanvas().style.cursor = "";
          setBattleAreaId(null);
        });

        setMapReady(true);
      });

      instance.on("error", (e) => {
        const w = window as unknown as { kartkrig?: { __tileErrors?: number } };
        if (w.kartkrig) w.kartkrig.__tileErrors = (w.kartkrig.__tileErrors ?? 0) + 1;
        console.error("map error", e.error?.message ?? e);
      });

      instance.on("click", (e) =>
        handleClick({ lat: e.lngLat.lat, lon: e.lngLat.lng }, instance.getZoom()),
      );

      (instance as unknown as { __tileErrors: number }).__tileErrors = 0;
      (window as unknown as { kartkrig?: unknown }).kartkrig = instance;
      map.current = instance;
    })();

    return () => {
      cancelled = true;
    };
  }, [handleClick]);

  // ── RESTORED AFTER A BAD EDIT ─────────────────────────────────────────────
  // An over-greedy replacement removed everything between the click handler and
  // the render, taking the polling effects and the action handlers with it.
  // Rebuilt here in the order the render expects them.

  useEffect(() => {
    const abort = new AbortController();
    const load = () => fetchHealth(abort.signal).then(setHealth).catch(() => setHealth(null));
    load();
    const timer = window.setInterval(load, 30_000);
    return () => {
      abort.abort();
      window.clearInterval(timer);
    };
  }, []);

  // Positions, often enough that movement reads as movement.
  useEffect(() => {
    if (!mapReady) return;
    const abort = new AbortController();
    const load = () =>
      fetchWorld(abort.signal)
        .then(setWorld)
        .catch(() => {
          // No theatre yet is a normal state, not an error to shout about.
        });
    load();
    // At 100× an hourly combat round is only 36 wall seconds. A one-second
    // report cadence keeps the displayed situation responsive without claiming
    // that the underlying combat model resolved another round.
    const timer = window.setInterval(load, 1000);
    return () => {
      abort.abort();
      window.clearInterval(timer);
    };
  }, [mapReady]);

  useEffect(() => {
    if (world) renderUnits(world.id, world.units, selected, symbolSize);
  }, [world, selected, symbolSize, renderUnits]);

  // ── SEA TRANSPORT EXISTS BEFORE THE UNIT ─────────────────────────────────
  // The durable queue gives both destination and arrival instant. A landing
  // ship is shown only during its final sixty game seconds and interpolated on
  // the theatre clock, so 1× is deliberately slow while 10× still reaches the
  // beach at the same historical instant.
  useEffect(() => {
    transportArrivals.current = world?.arrivals ?? [];
    const sampled = world?.game_time_s ?? clock?.game_time_s;
    if (sampled !== null && sampled !== undefined) {
      transportClock.current = {
        gameTimeS: sampled,
        sampledAt: performance.now(),
        rate: clock?.paused ? 0 : rate,
      };
    }
  }, [world, clock, rate]);

  useEffect(() => {
    const m = map.current;
    const sample = transportClock.current;
    if (!m || !mapReady || !sample) return;
    const now = sample.gameTimeS + ((performance.now() - sample.sampledAt) / 1000) * sample.rate;
    const visible = new Set<number>();
    for (const arrival of world?.arrivals ?? []) {
      const progress = seaTransitProgress(now, arrival.at_game_s);
      if (
        (arrival.mode !== "sea" && arrival.mode !== "mainland")
        || now < arrival.at_game_s - SEA_TRANSIT_GAME_S
        || now > arrival.at_game_s
      ) continue;
      visible.add(arrival.event_id);
      let marker = transportMarkers.current.get(arrival.event_id);
      const at = arrivalFrame("sea", arrival.lat, arrival.lon, progress);
      if (!marker) {
        const el = document.createElement("div");
        el.className = "unit-marker is-arriving is-arriving-sea";
        el.innerHTML = transportShipMarkup();
        el.title = `Landing ship carrying ${arrival.side} · ${arrival.name}`;
        marker = new maplibregl.Marker({ element: el, anchor: "center" })
          .setLngLat([at.lon, at.lat])
          .addTo(m);
        transportMarkers.current.set(arrival.event_id, marker);
      } else {
        marker.setLngLat([at.lon, at.lat]);
      }
    }
    for (const [id, marker] of transportMarkers.current) {
      if (!visible.has(id)) {
        marker.remove();
        transportMarkers.current.delete(id);
      }
    }
  }, [mapReady, world, clock, rate]);

  // ── HOW OFTEN THE ZONE IS REDRAWN ─────────────────────────────────────────
  // The zone is expensive: a road flood over up to 64 map tiles, a cross-country
  // reach at every point it finds, and a PostGIS union of the lot. It is not
  // something to poll at the movement rate.
  //
  // But it is not static either. It moves whenever a formation moves, and it
  // grows when a building finishes. So the rate follows the state rather than a
  // fixed number: **10 s while anything is marching, 45 s when the front is
  // still**, and immediately whenever something happens that is known to change
  // the shape — an arrival, a merge, a completed building, an order given.
  //
  // `zocKey` is what makes the last part work: it changes exactly when the set of
  // things that project a zone changes, which re-runs this effect and refetches
  // without waiting for the next tick.
  const marching = (world?.units ?? []).filter((u) => u.progress !== null).length;
  // `standing` counted finished buildings, because a completed one grew the
  // zone. Nothing does that now, so the key follows formations alone.
  const zocKey = `${world?.id ?? 0}-${world?.units.length ?? 0}-${marching}`;

  useEffect(() => {
    if (!mapReady || !world) return;
    const abort = new AbortController();
    // A rejected poll says nothing about who controls the ground. Retain the
    // last successful geometry until a newer successful report replaces it;
    // clearing it here was the client half of the enemy-ZOC blink.
    const load = () => fetchZoc(abort.signal).then(setZoc).catch(() => undefined);
    load();
    // The server keeps the zone current on its own now (owner, 2026-09-11:
    // "ZOC beräknas löpande") and serves the last result, so a poll is cheap and
    // can follow a march closely.
    const timer = window.setInterval(load, marching > 0 ? 3_000 : 12_000);
    return () => {
      abort.abort();
      window.clearInterval(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapReady, zocKey, zocNonce]);

  useEffect(() => {
    const empty = { type: "FeatureCollection" as const, features: [] };
    const paint = (id: string, geometry: GeoJSON.Geometry | null | undefined) => {
      const src = map.current?.getSource(id) as maplibregl.GeoJSONSource | undefined;
      src?.setData(geometry ? { type: "Feature", properties: {}, geometry } : empty);
    };
    paint("zoc", zoc?.area);
    paint("zoc-enemy", zoc?.enemy_area);
    paint("zoc-contested", zoc?.contested_area);
    paint("zoc-detached", zoc?.detached_area);

  }, [zoc]);

  // The mask only appears while a building is being placed.
  useEffect(() => {
    const m = map.current;
    if (!m || !mapReady || !m.getLayer("nobuild-fill")) return;
    m.setLayoutProperty(
      "nobuild-fill",
      "visibility",
      pending?.action === "build" ? "visible" : "none",
    );
  }, [pending, mapReady]);

  // The top bar and the log are what make an asynchronous game legible, so they
  // keep pace with positions rather than with zones — a clock that lags is
  // worse than none.
  useEffect(() => {
    if (!mapReady || !world) return;
    const abort = new AbortController();
    const load = () => {
      fetchClock(abort.signal).then(setClock).catch(() => setClock(null));
      fetchLog(abort.signal).then(setLog).catch(() => setLog(null));
    };
    load();
    const timer = window.setInterval(load, 5000);
    return () => {
      abort.abort();
      window.clearInterval(timer);
    };
  }, [mapReady, world?.id]);

  // ── THE CAMPAIGN ──────────────────────────────────────────────────────────
  // Polled on the same cadence as the economy. A scoreboard that lags behind the
  // stockpile would show a town as still yours seconds after the log said it
  // fell, and a player would trust neither.
  useEffect(() => {
    if (!mapReady || !world) return;
    const abort = new AbortController();
    const load = () => {
      fetchCampaign(abort.signal).then(setCampaign).catch(() => setCampaign(null));
    };
    load();
    const timer = window.setInterval(load, 5000);
    return () => {
      abort.abort();
      window.clearInterval(timer);
    };
  }, [mapReady, world?.id]);

  useEffect(() => {
    const m = map.current;
    if (!m || !mapReady) return;
    const source = m.getSource("objectives") as maplibregl.GeoJSONSource | undefined;
    if (!source) return;
    source.setData({
      type: "FeatureCollection",
      features: (campaign?.objectives ?? []).map((o) => ({
        type: "Feature" as const,
        geometry: { type: "Point" as const, coordinates: [o.lon, o.lat] },
        properties: {
          label: o.name,
          mine: o.mine,
          // Held by somebody, and that somebody is not you. Computed here rather
          // than sent, because "enemy" is the absence of two facts and the
          // server already sends both.
          enemy: o.holder !== null && !o.mine,
          weight: o.weight,
          // 41 of the 111 places are Husky's own ground. The other 70 exist to
          // colour the map, so the difference has to be visible without reading
          // a label.
          key: o.key_position,
        },
      })),
    });
  }, [campaign, mapReady]);

  // Imagery configuration comes from the server, so the provider hostname stays
  // in exactly one file — the rule the basemap chain already follows.
  useEffect(() => {
    const abort = new AbortController();
    fetchBasemaps(abort.signal)
      .then((cfg) => {
        if (!cfg.aerial) return;
        setAerialInfo({ attribution: cfg.aerial.attribution, licence: cfg.aerial.licence });
        const source = map.current?.getSource("aerial") as
          | maplibregl.RasterTileSource
          | undefined;
        source?.setTiles([cfg.aerial.tile_url]);

        // The period map: added once its configuration is known, so its zoom
        // range comes from the server that enforces it rather than a copy.
        const h = cfg.historical;
        const m = map.current;
        if (h && m && !m.getSource("historic")) {
          setHistoricInfo({ attribution: h.attribution, licence: h.licence, minZoom: h.min_zoom });
          m.addSource("historic", {
            type: "raster",
            tiles: [h.tile_url],
            tileSize: 256,
            minzoom: h.min_zoom,
            maxzoom: h.max_zoom,
          });
          // Beneath the 1943 sheets when they are already there, which they
          // are when the 1943 view opens the game; otherwise beneath the roads.
          const beneath = m.getLayer("ams-layer-100k")
            ? "ams-layer-100k"
            : m.getLayer("roads-minor")
              ? "roads-minor"
              : undefined;
          m.addLayer(
            {
              id: "historic-layer",
              type: "raster",
              source: "historic",
              minzoom: h.min_zoom,
              layout: { visibility: "none" },
              paint: { "raster-opacity": 1 },
            },
            beneath,
          );
        }
      })
      .catch(() => setAerialInfo(null));
    return () => abort.abort();
  }, [mapReady]);

  useEffect(() => {
    const m = map.current;
    if (!m || !mapReady) return;
    if (m.getLayer("aerial-layer")) {
      m.setLayoutProperty("aerial-layer", "visibility", aerial ? "visible" : "none");
      // ── THE ROADS CHANGE COLOUR WITH THE GROUND UNDER THEM ────────────────
      // Owner, 2026-09-17: roads were hard to see over the photo. They were
      // drawing in the right place — the imagery is anchored beneath
      // `roads-minor` — but in dark slate, which is right for the dark vector
      // map and invisible on a photograph.
      //
      // Switched rather than compromised: a single colour bright enough for the
      // photo glares on the map, and one dark enough for the map disappears on
      // the photo. Each layer is checked before it is painted, because the blank
      // fallback style has no road layers at all and naming a missing layer
      // throws — the same trap the imagery anchor above already guards against.
      const roads = aerial ? ROADS_OVER_IMAGERY : ROADS_OVER_MAP;
      if (m.getLayer("roads-major")) {
        m.setPaintProperty("roads-major", "line-color", roads.major);
      }
      if (m.getLayer("roads-minor")) {
        m.setPaintProperty("roads-minor", "line-color", roads.minor);
      }
    }
  }, [aerial, mapReady]);

  useEffect(() => {
    const m = map.current;
    if (!m || !mapReady) return;
    // The IGM layer is no longer part of the 1943 view (owner, 2026-09-25:
    // "Undvik IGM"); it is kept hidden.
    if (m.getLayer("historic-layer")) {
      m.setLayoutProperty("historic-layer", "visibility", "none");
    }
    if (m.getLayer("place-labels")) {
      m.setLayerZoomRange("place-labels", 0, ams ? AMS_MIN_ZOOM : 24);
    }
    // ── NO MODERN ROADS OVER THE OLD MAPS ─────────────────────────────────
    // Owner, 2026-09-25: *"Kan du ta bort det moderna vägnätet som ligger
    // ovanpå bilderna?"* — after trying today's roads over the 1943 sheets.
    // Hidden wherever the old maps are drawn, shown below zoom 8 where they
    // are not. Only the drawing goes: formations still march on today's
    // network, and a march's own route line is still drawn.
    for (const id of ["roads-minor", "roads-major", "rail"]) {
      if (m.getLayer(id)) m.setLayerZoomRange(id, 0, ams ? AMS_MIN_ZOOM : 24);
    }
  }, [ams, mapReady, historicInfo]);

  // ── THE 1943 SHEETS ───────────────────────────────────────────────────────
  // Added on first use rather than at start: tiles nobody asked for should
  // not be requested. Above the IGM layer when both are on — the wartime
  // sheet is the one being asked for — and beneath the roads.
  useEffect(() => {
    const m = map.current;
    if (!m || !mapReady) return;
    if (ams && !m.getSource("ams-100k")) {
      // Coarsest first: each is added beneath the roads, so the later (finer)
      // one lands above the earlier.
      for (const layer of AMS_LAYERS) {
        m.addSource(`ams-${layer.series}`, {
          type: "raster",
          tiles: [`${window.location.origin}${amsTiles(layer.series)}`],
          tileSize: 256,
          bounds: AMS_BOUNDS,
          minzoom: layer.cut[0],
          maxzoom: layer.cut[1],
        });
        m.addLayer(
          {
            id: `ams-layer-${layer.series}`,
            type: "raster",
            source: `ams-${layer.series}`,
            minzoom: layer.from,
            paint: { "raster-opacity": 1 },
          },
          m.getLayer("roads-minor") ? "roads-minor" : undefined,
        );
      }
    }
    for (const layer of AMS_LAYERS) {
      if (m.getLayer(`ams-layer-${layer.series}`)) {
        m.setLayoutProperty(`ams-layer-${layer.series}`, "visibility", ams ? "visible" : "none");
      }
    }
  }, [ams, mapReady]);

  // The building catalogue went with `/api/buildings`. It was fetched once and
  // supplied the requirement list shown beside a placement, from the server's
  // own rules rather than a table kept here. One building is left, and its rule
  // is about standing on an objective ring.

  // ── THE ZONE NOTE BELONGS IN THE LOG TOO ──────────────────────────────────
  // Owner, 2026-09-10, on the line in the theatre bar: *"vad betyder den och
  // behöver vi visa den?"*
  //
  // It means the zone shown is not the zone the model would draw — some
  // formation reached no road network and fell back to a bare circle. That is
  // worth knowing and was worth keeping, but not as a permanent strip along the
  // bottom of the screen: it is an event, it is usually the same event twice in
  // a row, and a warning that is always on is a warning nobody reads.
  //
  // So it is logged when it *changes*, and the bar goes back to naming the
  // theatre. Which is also how the bug behind it was found — the note was
  // telling the truth, and two formations really had no roads.
  // The setting lives on the server; the client only mirrors it. Seeded from
  // the zone report so opening the menu shows what is in force even after a
  // reload — otherwise the slider would claim 100% on a theatre set to 40%.
  useEffect(() => {
    if (zoc?.zoc) setZocSettings(zoc.zoc);
  }, [zoc?.zoc]);

  // The reference page went with `/api/reference` — it listed every building and
  // branch with the costs, upkeep and yields that governed them, and none of
  // those exist now.

  // Same argument for speed: the server owns it, the buttons only mirror it.
  useEffect(() => {
    if (clock?.rate != null) setRate(clock.rate);
  }, [clock?.rate]);

  const lastZoneNote = useRef<string | null>(null);
  useEffect(() => {
    const note = zoc?.note ?? null;
    if (note === lastZoneNote.current) return;
    lastZoneNote.current = note;
    if (note) say("zone", note);
  }, [zoc?.note, say]);

  // ── THE SERVER'S EVENTS, AS THEY HAPPEN ───────────────────────────────────
  // Only entries that arrive after the page has loaded are shown: the first
  // poll marks everything already there as seen, so opening a game does not
  // fire a volley of old news. (`feed`, which merged the two streams into the
  // log panel, went with the panel on 2026-09-25.)
  const seenLog = useRef<Set<string> | null>(null);
  useEffect(() => {
    if (!log) return;
    const keys = log.entries.map((e) => `${e.at_game_s}|${e.text}`);
    if (seenLog.current === null) {
      seenLog.current = new Set(keys);
      return;
    }
    // Oldest first, so a burst reads in the order it happened.
    for (const [i, e] of [...log.entries.entries()].reverse()) {
      if (seenLog.current.has(keys[i])) continue;
      seenLog.current.add(keys[i]);
      toast(e.kind, e.text);
    }
  }, [log, toast]);

  // ── WHAT THE SCOREBOARD SAYS IN ONE LINE ──────────────────────────────────
  // Computed here rather than in the markup so the top bar stays readable and
  // so the "behind by" figure has one definition. A player checking whether they
  // are winning should not have to subtract two numbers themselves.
  const mine = campaign?.standings.find((s) => s.mine) ?? null;
  const heldByMe = (campaign?.objectives ?? []).filter((o) => o.mine).length;
  const rivals = (campaign?.standings ?? []).filter((s) => !s.mine);
  const bestRival = rivals.reduce<typeof rivals[number] | null>(
    (best, s) => (best === null || s.points > best.points ? s : best),
    null,
  );
  const leaderGap =
    mine && bestRival
      ? ` · ${mine.points >= bestRival.points ? "ahead by" : "behind by"} ${Math.round(
          Math.abs(mine.points - bestRival.points),
        ).toLocaleString("en")}`
      : "";
  const standingsTitle = (campaign?.standings ?? [])
    .map((s) => `${s.name}: ${Math.round(s.points).toLocaleString("en")} pts, ${s.formations} formations, ${s.evacuated} evacuated`)
    .join("\n");
  const phase = world ? operationPhase(world.arrivals ?? [], campaign?.objectives ?? [], campaign?.game_time_s) : null;
  const servedPhase = currentPhase(campaign?.phases);
  const nextAt = world?.arrivals?.[0]?.at_game_s ?? null;
  const nextArrivals = nextAt === null
    ? []
    : (world?.arrivals ?? []).filter((arrival) => arrival.at_game_s === nextAt);
  const reinforcementTitle = (world?.arrivals ?? [])
    .map((arrival) => `${arrival.side} · ${arrival.name} — ${arrival.mode} — ${formatCampaignTime(arrival.at_game_s)}`)
    .join("\n");

  // Guarded twice — here as well as at the click — so no other path that sets
  // `menuFor` can open orders on a formation that is not his.
  const menuUnit = world?.units.find((u) => u.id === menuFor && u.mine) ?? null;

  // ── KEEPING THE MENU ON THE UNIT ──────────────────────────────────────────
  // The menu is positioned in screen pixels, so it has to be re-projected
  // whenever the map moves or the unit does — otherwise panning slides the map
  // out from under it and it ends up pointing at empty ground.
  //
  // `move` fires continuously during a drag, which is what keeps it attached
  // rather than jumping to the new position when the drag ends.
  const [menuAt, setMenuAt] = useState<{ x: number; y: number } | null>(null);
  // `openSub` tracked which submenu was folded out, Kartbord-style, and was
  // reset whenever the menu changed unit — otherwise a submenu opened for one
  // formation stayed open over the next. With one building and nothing to raise
  // there is no submenu left to track.

  // ── SUPPLY AND ORDERS, ON HOVER ───────────────────────────────────────────
  // Owner, 2026-09-10: *"Enhetens Supply och current order ska visas när man
  // hovrar."*
  //
  // These used to be in the menu, which meant reading the state of a formation
  // cost a click and covered the map. On hover they cost nothing and are gone
  // again the moment you look elsewhere — which is the right weight for
  // something you check constantly and act on rarely.
  //
  // Kept as a rendered element rather than the browser's `title` tooltip: that
  // one waits a second before appearing, cannot be coloured, and does not exist
  // on a phone. The marker keeps a `title` as well, so the information is still
  // reachable by a screen reader.
  const [hover, setHover] = useState<{ id: number; x: number; y: number } | null>(null);
  const [battleAreaId, setBattleAreaId] = useState<number | null>(null);
  const [phasePanelOpen, setPhasePanelOpen] = useState(false);
  // Owner, 2026-09-25: *"Jag vill kunna klicka på reinforcements för att se
  // alla planerade egna och fiendens."*
  const [reinforcementsOpen, setReinforcementsOpen] = useState(false);
  // ── AIR SUPPORT (Sicily.md §36) ────────────────────────────────────────
  // Arming it turns the next click on an enemy formation into a strike. A ref
  // as well as state: the marker click handlers are created once and read it.
  const [airArmed, setAirArmed] = useState(false);
  airArmedRef.current = airArmed;
  const [hoveredPhase, setHoveredPhase] = useState<number | null>(null);
  useEffect(() => {
    const source = map.current?.getSource("phase-highlight") as maplibregl.GeoJSONSource | undefined;
    source?.setData(phaseHighlight(campaign?.phases?.find((p) => p.number === hoveredPhase)));
  }, [mapReady, campaign, hoveredPhase]);
  const hoverUnit = world?.units.find((u) => u.id === hover?.id) ?? null;

  // ── THE BATTLE REPORT ─────────────────────────────────────────────────────
  // Owner, 2026-09-12: *"Vid varje strid ska man kunna hovra över enhet i strid
  // (egen eller motståndarens) och få upp en 'Battle report' meny till höger på
  // skärmen. … Detta ska uppdateras ofta."*
  //
  // Asked for whichever formation is hovered, without checking first whether it
  // is fighting: an enemy's combat state is redacted out of the unit feed, so
  // the client genuinely cannot know. A 404 is the ordinary answer and clears
  // the panel rather than being reported as a failure.
  const [battle, setBattle] = useState<BattleReport | null>(null);
  const hoveredId = battleAreaId ?? hover?.id ?? null;
  useEffect(() => {
    if (hoveredId === null) {
      setBattle(null);
      return;
    }
    const abort = new AbortController();
    const load = () =>
      fetchBattle(hoveredId, abort.signal)
        .then(setBattle)
        .catch(() => setBattle(null));
    load();
    // The same cadence the world is polled at: a fight that has moved on while
    // you watch is the thing the panel exists to show.
    const timer = window.setInterval(load, 1000);
    return () => {
      abort.abort();
      window.clearInterval(timer);
    };
  }, [hoveredId]);
  useEffect(() => {
    const m = map.current;
    if (!m || !mapReady || !menuUnit) {
      setMenuAt(null);
      return;
    }
    const place = () => {
      const p = m.project([menuUnit.lon, menuUnit.lat]);
      setMenuAt({ x: p.x, y: p.y });
    };
    place();
    m.on("move", place);
    m.on("resize", place);
    return () => {
      m.off("move", place);
      m.off("resize", place);
    };
  }, [mapReady, menuUnit?.id, menuUnit?.lat, menuUnit?.lon, menuUnit]);

  // An open heap belongs to the view it was opened in: panning or zooming
  // moves every symbol, and a ring left behind would point at nothing.
  useEffect(() => {
    const m = map.current;
    if (!m || !mapReady) return;
    const fold = () => foldHeap();
    m.on("movestart", fold);
    m.on("zoomstart", fold);
    return () => {
      m.off("movestart", fold);
      m.off("zoomstart", fold);
    };
  }, [mapReady]);

  // ── POSITIONS BETWEEN POLLS ───────────────────────────────────────────────
  // Owner, 2026-09-11: *"Låt webbläsare beräkna position mellan hämtningar."*
  // The world arrives every three seconds. In between, every marching symbol
  // walks its own route at the theatre's speed — still when paused — by the
  // server's own rule (`units::position_along`, mirrored in map/motion.ts), so
  // the next poll lands where the symbol already is rather than pulling it back.
  const polledAt = useRef<{ units: WorldReport["units"]; at: number } | null>(null);
  useEffect(() => {
    if (world) polledAt.current = { units: world.units, at: performance.now() };
  }, [world]);
  const gameRateRef = useRef(1);
  gameRateRef.current = clock?.paused ? 0 : rate;
  useEffect(() => {
    if (!mapReady) return;
    let frame = 0;
    let last = -Infinity;
    const tick = (t: number) => {
      // Thirty updates a second: smooth to the eye, cheap on a phone.
      if (t - last >= 33) {
        last = t;
        const snap = polledAt.current;
        if (snap) {
          const wallS = (performance.now() - snap.at) / 1000;
          for (const u of snap.units) {
            if (arrivalAnimations.current.has(u.id)) continue;
            if (u.progress === null) continue;
            const mk = unitMarkers.current.get(u.id);
            if (!mk) continue;
            const [lat, lon] = glide(u, wallS, gameRateRef.current);
            mk.setLngLat([lon, lat]);
          }
        }
        for (const [id, arrival] of arrivalAnimations.current) {
          const mk = unitMarkers.current.get(id);
          if (!mk) {
            arrivalAnimations.current.delete(id);
            continue;
          }
          const p = Math.min(
            1,
            (performance.now() - arrival.startedAt) / arrivalDuration(arrival.mode),
          );
          const at = arrivalFrame(
            arrival.mode,
            arrival.target.lat,
            arrival.target.lon,
            p,
          );
          mk.setLngLat([at.lon, at.lat]);

          if (arrival.mode === "air") {
            const eased = p * p * (3 - 2 * p);
            const sway = Math.sin(p * Math.PI * 5) * 10 * (1 - p);
            const flight = mk.getElement().querySelector<HTMLElement>(".arrival-flight");
            if (flight) {
              flight.style.transform = `translate3d(${sway}px, ${-180 * (1 - eased)}px, 0)`;
            }
            const canopy = mk.getElement().querySelector<HTMLElement>(".arrival-parachute");
            if (canopy && p > 0.82) canopy.style.opacity = String((1 - p) / 0.18);
          }

          if (p >= 1) {
            const el = mk.getElement();
            mk.setLngLat([arrival.target.lon, arrival.target.lat]);
            el.classList.remove("is-arriving", `is-arriving-${arrival.mode}`);
            el.innerHTML = arrival.finalHtml;
            el.dataset.state = arrival.finalState;
            arrivalAnimations.current.delete(id);
          }
        }
        const transportSample = transportClock.current;
        if (transportSample) {
          const now = transportSample.gameTimeS
            + ((performance.now() - transportSample.sampledAt) / 1000) * transportSample.rate;
          for (const arrival of transportArrivals.current) {
            const mk = transportMarkers.current.get(arrival.event_id);
            if (!mk || arrival.mode !== "sea") continue;
            const p = seaTransitProgress(now, arrival.at_game_s);
            const at = arrivalFrame("sea", arrival.lat, arrival.lon, p);
            mk.setLngLat([at.lon, at.lat]);
          }
        }
      }
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [mapReady]);

  useEffect(() => {
    const m = map.current;
    if (!m || !mapReady) return;
    const src = m.getSource("demolitions") as maplibregl.GeoJSONSource | undefined;
    if (!src) return;
    src.setData({
      type: "FeatureCollection",
      features: (world?.demolitions ?? []).map(([lat, lon]) => ({
        type: "Feature",
        properties: {},
        geometry: { type: "Point", coordinates: [lon, lat] },
      })),
    });
  }, [world, mapReady]);

  // ── FIGHTS ON THE MAP ─────────────────────────────────────────────────────
  // One dashed battle area per defender-centred fight. Pairwise contacts are
  // grouped first; the circle then encloses every current participant and
  // carries the defender id that names the shared live report.
  useEffect(() => {
    const m = map.current;
    if (!m || !mapReady) return;
    const src = m.getSource("engagements") as maplibregl.GeoJSONSource | undefined;
    if (!src) return;
    const byId = new Map((world?.units ?? []).map((u) => [u.id, u]));
    src.setData({
      type: "FeatureCollection",
      features: [...(world?.engagements ?? []).reduce((groups, [attacker, defender]) => {
        const ids = groups.get(defender) ?? new Set<number>([defender]);
        ids.add(attacker);
        groups.set(defender, ids);
        return groups;
      }, new Map<number, Set<number>>())].flatMap(([defender, ids]) => {
        const units = [...ids].map((id) => byId.get(id)).filter((u): u is Unit => Boolean(u));
        if (units.length < 2) return [];
        const centre = units.reduce((p, u) => ({ lon: p.lon + u.lon / units.length, lat: p.lat + u.lat / units.length }), { lon: 0, lat: 0 });
        const cos = Math.max(0.2, Math.cos(centre.lat * Math.PI / 180));
        const radius = Math.max(0.009, ...units.map((u) => Math.hypot((u.lon - centre.lon) * cos, u.lat - centre.lat))) + 0.006;
        const ring = Array.from({ length: 33 }, (_, i) => {
          const angle = i / 32 * Math.PI * 2;
          return [centre.lon + Math.cos(angle) * radius / cos, centre.lat + Math.sin(angle) * radius];
        });
        return [{
          type: "Feature" as const,
          properties: { battle_id: defender },
          geometry: { type: "Polygon" as const, coordinates: [ring] },
        }];
      }),
    });
  }, [mapReady, world]);

  // ── THE SELECTED FORMATION'S FIRING RANGE ─────────────────────────────────
  // Owner, 2026-09-11: *"Jag bad om en range-ring när jag klickar på enhet som
  // påvisar hur långt den skjuter."* The base range from the server, drawn as
  // a circle on the ground. It is the figure live combat uses today: the worker
  // resolves on open ground at zero height, so terrain and height do not yet
  // change it (see the note Kartkrig, 2026-09-11).
  //
  // Owner, later the same evening: *"Ändra så att ring för skjutavstånd visas
  // när vi hovrar över enhet istället för vid klick."* A phone has no hover, so
  // there the ring still follows the tap — otherwise it could never be seen.
  const rangeTarget = canHover ? (hover?.id ?? null) : selected;
  const rangeUnit = world?.units.find((u) => u.id === rangeTarget) ?? null;
  useEffect(() => {
    const m = map.current;
    if (!m || !mapReady) return;
    const src = m.getSource("range") as maplibregl.GeoJSONSource | undefined;
    const sup = m.getSource("support-range") as maplibregl.GeoJSONSource | undefined;
    if (!src) return;
    const ring = (lat: number, lon: number, radius: number, mine: boolean): GeoJSON.FeatureCollection => ({
      type: "FeatureCollection",
      features:
        radius > 0
          ? [
              {
                type: "Feature",
                properties: { mine },
                geometry: { type: "Polygon", coordinates: [rangeRing(lat, lon, radius)] },
              },
            ]
          : [],
    });
    if (!rangeUnit || !(rangeUnit.range_m > 0)) {
      src.setData({ type: "FeatureCollection", features: [] });
      sup?.setData({ type: "FeatureCollection", features: [] });
      return;
    }
    // Direct fire breathes; support fire (a gun line's 22 km) is a quiet dashed
    // ring further out, so the two reaches are never read as one.
    const draw = (lat: number, lon: number, u: NonNullable<typeof rangeUnit>) => {
      src.setData(ring(lat, lon, u.range_m, u.mine));
      sup?.setData(ring(lat, lon, u.support_range_m ?? 0, u.mine));
    };
    draw(rangeUnit.lat, rangeUnit.lon, rangeUnit);
    // About twenty updates a second: a smooth breath without asking the map to
    // repaint sixty times a second on a phone.
    let frame = 0;
    let last = -Infinity;
    let lastCentre = -Infinity;
    const target = rangeUnit;
    const tick = (t: number) => {
      if (t - last >= 50 && m.getLayer("range-line")) {
        const pulse = ringPulse(t);
        m.setPaintProperty("range-line", "line-opacity", pulse.opacity);
        m.setPaintProperty("range-line", "line-width", pulse.width);
        last = t;
      }
      // A marching formation's rings walk with it between polls, as its symbol does.
      if (target.progress !== null && t - lastCentre >= 200) {
        lastCentre = t;
        const snap = polledAt.current;
        const u = snap?.units.find((x) => x.id === target.id);
        if (snap && u) {
          const [lat, lon] = glide(u, (performance.now() - snap.at) / 1000, gameRateRef.current);
          draw(lat, lon, u);
        }
      }
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [mapReady, rangeUnit?.id, rangeUnit?.lat, rangeUnit?.lon, rangeUnit?.range_m, rangeUnit?.support_range_m, rangeUnit?.mine]);

  // ── WHAT THIS FORMATION MAY BE ORDERED TO DO ──────────────────────────────
  // The same two rules the server enforces in `Branch::may`. Stated here so the
  // interface can leave out what it must not offer — not as a second gate: if
  // these ever drift apart the server refuses and says why, which is the failure
  // mode you want rather than a silent one.
  // Owner, 2026-09-11: supply companies build too — supply points on the rings.
  // `canBuild` went with the supply points. It narrowed the action to the
  // supply arm, because offering an engineer a menu the server would refuse is
  // a lie the interface tells on the server's behalf.

  // Everything of the same branch and echelon standing close enough to merge.
  // Computed here rather than inside the action so the button can show the count
  // and disappear when there is nothing to join.
  const joinCandidates = menuUnit
    ? (world?.units ?? []).filter(
        (u) =>
          u.branch === menuUnit.branch &&
          u.echelon === menuUnit.echelon &&
          Math.hypot(u.lat - menuUnit.lat, u.lon - menuUnit.lon) < 0.09,
      )
    : [];

  /** Report an outcome the same way whatever it was.
   *
   * A refusal is not an error: the design is that the game says why, in the
   * same place, whether it went through or not. */
  strikeRef.current = (targetId: number, name: string) => {
    setAirArmed(false);
    void airStrike(targetId)
      .then((r) => {
        if (r.ok) say("order", `Aircraft strike ${name}.`);
        else say("refused", ...r.refused);
        return fetchWorld().then(setWorld);
      })
      .catch((e) => say("refused", e instanceof Error ? e.message : "Air strike failed"));
  };
  const report = (r: { ok: boolean; refused: string[] }, done: string) => {
    if (r.ok) say("order", done);
    else say("refused", ...r.refused);
    if (r.ok) {
      setMenuFor(null);
      // Anything that went through may have changed the shape of the front.
      // Cheaper than shortening the poll for everyone: ask once, now.
      setZocNonce((n) => n + 1);
    }
  };

  const runAction = async (action: string, arg?: string) => {
    if (!menuUnit) return;
    // Move and build need somewhere to point at, so they arm a map click rather
    // than firing where the unit already stands.
    if (action === "move" || action === "build") {
      setPending({ action, arg });
      setMenuFor(null);
      if (action === "move") {
        say(
          "order",
          arg === "ground"
            ? "Click a destination — force move: the shortest way across the ground."
            : "Click a destination — move: the quickest way, by road or across the ground.",
        );
      } else {
        // Shaded ground is refused ground; the rest of the requirement is about
        // the point itself, which the shading cannot know. Stated together so
        // the two halves of the answer arrive as one.
        //
        // The requirement list and the price came from `/api/buildings` and went
        // with it. The rule is now short enough to say outright — and the server
        // still answers with the full checklist if the click misses.
        say(
          "order",
          "Click the objective ring to set up the supply point.",
          "Shaded ground is outside your control.",
          "It has to stand on a ring nobody has claimed.",
        );
      }
      return;
    }
    setActing(true);
    try {
      if (action === "split") {
        report(await splitUnit(menuUnit.id), "Split.");
      } else if (action === "join") {
        // The same list the button counted. Picking them by hand on a phone
        // would be unusable, and the merge rules refuse anything invalid anyway
        // — so the refusal explains rather than the interface pre-empting it.
        report(await mergeUnits(joinCandidates.map((u) => u.id)), "Formations joined.");
      } else if (action === "stance" && arg) {
        report(
          await setCombatStance(menuUnit.id, arg),
          `Combat stance: ${arg.replaceAll("_", " ")}.`,
        );
      }
    } catch (e) {
      say("refused", e instanceof Error ? e.message : "That did not work");
    } finally {
      setActing(false);
    }
  };

  const changeRate = async (asked: number) => {
    setClockBusy(true);
    try {
      const r = await apiSetRate(asked);
      setRate(r.rate);
      if (r.rate !== asked) {
        say("order", `Speed ${asked}× is not available — running at ${r.rate}×.`);
      }
    } catch (e) {
      say("refused", e instanceof Error ? e.message : "Could not change the speed");
    } finally {
      setClockBusy(false);
    }
  };

  // `loadCensus` went with `/api/census`.

  const changeZoc = async (patch: Partial<ZocSettings>) => {
    try {
      const r = await apiSetSettings(patch);
      setZocSettings(r.zoc);
      // The zone is what these settings are for, so it is refetched at once
      // rather than waiting up to 45 s for the next poll to show the effect.
      setZocNonce((n) => n + 1);
      const key = Object.keys(patch)[0] as keyof ZocSettings | undefined;
      if (key && Math.abs((r.zoc[key] as number) - (patch[key] as number)) > 1e-6) {
        say("order", `${key.replace(/_/g, " ")} clamped to ${r.zoc[key]}.`);
      }
    } catch (e) {
      say("refused", e instanceof Error ? e.message : "Could not save the setting");
    }
  };

  const togglePause = async () => {
    if (!clock) return;
    setClockBusy(true);
    try {
      const r = await setPause(!clock.paused);
      if (r.refused) say("refused", r.refused);
      setClock({ ...clock, paused: r.paused, game_time_s: r.game_time_s });
    } catch (e) {
      say("refused", e instanceof Error ? e.message : "Could not change the clock");
    } finally {
      setClockBusy(false);
    }
  };

  const [confirmReset, setConfirmReset] = useState(false);

  const resetGame = async () => {
    // Two clicks, because the first version deleted a live theatre on one, with
    // no undo, from the same panel as the unit list.
    if (!confirmReset) {
      setConfirmReset(true);
      window.setTimeout(() => setConfirmReset(false), 5000);
      return;
    }
    setConfirmReset(false);
    setResetting(true);
    try {
      await resetWorlds();
      setWorld(null);
      setZoc(null);
      setSelected(null);
      setMenuFor(null);
      setToasts([]);
      onLeave(true);
    } catch (e) {
      say("refused", e instanceof Error ? e.message : "Reset failed");
    } finally {
      setResetting(false);
    }
  };

  return (
    <div className="app">
      <div ref={container} className="map" />
      {!mapReady && <div className="booting">Loading the world…</div>}
      {briefingOpen ? (
        <section className="briefing" role="dialog" aria-label="Allied operation briefing">
          <h2>Allied briefing — Operation Husky</h2>
          <p>Land, secure Gela, Licata and Siracusa, then drive north and east. Capture and hold ground; waiting alone earns nothing.</p>
          <ol>
            <li><b>9–10 Jul · Invasion:</b> establish the beachheads.</li>
            <li><b>11–15 Jul · Counterattack:</b> contain Axis counterattacks.</li>
            <li><b>16–24 Jul · Breakout:</b> keep the advance moving.</li>
            <li><b>25 Jul–5 Aug · Withdrawal:</b> break the Etna–Catania line.</li>
            <li><b>6–10 Aug · Evacuation preparation:</b> take Messina or trap Germans trying to leave.</li>
            <li><b>11–17 Aug · Evacuation:</b> secure Messina before the Axis can save more German formations.</li>
          </ol>
          <p>Win early by taking 90% of Sicily, or earn the best historical verdict through territory, time, Axis losses and preventing evacuation. Enemy ZOC cuts supply and blocks corridors between your held points.</p>
          <button type="button" onClick={() => setBriefingOpen(false)}>Begin operation</button>
        </section>
      ) : null}
      {/* ── THE END OF A CAMPAIGN ────────────────────────────────────────────
          A game that cannot end has no tension in any of its decisions. This is
          the moment that makes every earlier one mean something, so it is stated
          plainly and it does not disappear on its own — the theatre stops
          advancing behind it and the map is left exactly as it finished. */}
      {campaign?.outcome ? (
        <div className={`verdict ${campaign.winner_nation_id === mine?.nation_id ? "won" : "lost"}`}>
          <b>
            {campaign.winner_nation_id === mine?.nation_id ? "Victory" : "Defeat"}
          </b>
          <span>
            {(campaign.standings.find((s) => s.nation_id === campaign.winner_nation_id)?.name ??
              "A nation")}{" "}
            {campaign.outcome === "allied_decisive"
              ? "won a decisive Allied victory."
              : campaign.outcome === "allied"
                ? "won the Sicilian campaign."
                : campaign.outcome === "axis_operational"
                  ? "saved enough of the Axis army to achieve its operational aim."
                  : campaign.outcome === "axis_major"
                    ? "destroyed the invasion force."
                    : campaign.outcome === "last_standing"
                      ? "holds the field — every other formation is gone."
                      : "reached the objective target."}
          </span>
          <i>
            {campaign.standings
              .map((s) => `${s.name} ${Math.round(s.points).toLocaleString("en")}`)
              .join("  ·  ")}
          </i>
        </div>
      ) : null}

      {/* ── THE TOP BAR ────────────────────────────────────────────────────
          The mockup's own layout: what you have, what it earns, what it costs,
          and how long the position holds. The maintenance figure is shown as
          current over ceiling because that is the readable form — overshooting
          it is allowed, and the clock beside it is how long that can last. */}
      {/* The bar is always there. It used to appear only once a theatre existed,
          which meant the imagery toggle — the one control that helps you *choose*
          where to begin — was unavailable at precisely the moment you were
          choosing. The readouts still wait for something to read. */}
      <div className="topbar">
          {/* ── ONE BAR, NOT THREE ─────────────────────────────────────────
              Owner, 2026-09-09: remove the Data chain panel and the Kartkrig
              panel. Both were built for a phase where the question was "does the
              data chain work" — a developer's question, permanently occupying
              the corner of a game.

              What was worth keeping came here instead of being deleted with the
              rest: the build note and the client-versus-server check, because a
              stale client is a real failure that a single version number cannot
              show; and the imagery toggle, which is a thing the map does rather
              than a status readout about it. */}
          <span className="force">
            <b>Kartkrig</b>
            <em
              className="ver"
              title={`${BUILD_NOTE}${health?.version ? ` · server ${health.version}` : ""}`}
            >
              {BUILD}
              {health?.version && health.version !== BUILD ? (
                <i className="drift" title={`Client ${BUILD}, server ${health.version}`}>
                  {" ≠ "}
                  {health.version}
                </i>
              ) : null}
            </em>
          </span>
          {/* The zoom level, in the open. Asked for 2026-09-15 so a sensible
              limit can be named from what is actually on screen rather than
              guessed at from a number nobody could see. */}
          <span className="res">
            <b>Zoom</b>
            <em>{zoomLevel === null ? "—" : `z${zoomLevel.toFixed(2)}`}</em>
          </span>
          {/* ── THE RESOURCE ROW WENT WITH THE ECONOMY ──────────────────────
              Three stockpiles with their hourly net, the maintenance ceiling as
              `420 / 600` with a *covers 31 h* clock under it, and staff used
              against capacity. It was the readout the whole RTS pressure model
              spoke through.

              Nothing replaces it yet, and that is worth seeing rather than
              papering over: the top bar now carries the theatre, the zoom, the
              campaign score and the clock. What a player cannot read anywhere is
              their supply state — which is step 14's to answer, from the beaches
              and ports rather than from a stockpile. */}
          {/* ── THE SCOREBOARD ──────────────────────────────────────────────
              Points and towns, not a percentage. A percentage of what is held
              says nothing about how long it has been held, and holding over time
              is the whole thing the campaign measures.

              The target is read from the server rather than assumed: it is
              derived from what this theatre actually contains, so it differs in
              every war. */}
          {campaign && campaign.objectives.length > 0 ? (
            <span className="res campaign" title={standingsTitle}>
              <b>Campaign</b>
              <em>
                {Math.round(mine?.points ?? 0).toLocaleString("en")} /{" "}
                {Math.round(campaign.target).toLocaleString("en")}
              </em>
              <i>
                {heldByMe} of {campaign.objectives.length} towns
                {leaderGap}
              </i>
            </span>
          ) : null}
          {phase ? (
            // Owner, 2026-09-22: the phase readout opens the list of every
            // phase, and hovering it lights the current phase up on the map.
            <button
              type="button"
              className={`res operation-phase${phasePanelOpen ? " open" : ""}`}
              title="Show every phase"
              onClick={() => setPhasePanelOpen((open) => !open)}
              onMouseEnter={() => setHoveredPhase(servedPhase?.number ?? phase.number)}
              onMouseLeave={() => setHoveredPhase(null)}
            >
              <b>Phase {servedPhase?.number ?? phase.number} / 6 {medalStrip(campaign?.phases)}</b>
              <em>{servedPhase?.name ?? phase.name}</em>
              <i>
                {servedPhase
                  ? `${phaseProgress(servedPhase).held}/${phaseProgress(servedPhase).total} · ${servedPhase.allied_goal}`
                  : `Next: ${phase.trigger}`}
              </i>
            </button>
          ) : null}
          {world && typeof world.air_points === "number" ? (
            <button
              type="button"
              className={`res air-support${airArmed ? " armed" : ""}`}
              disabled={world.air_points < 1 && !airArmed}
              title="Air support (Sicily.md §36): one point strikes one enemy formation you can see. Click, then click the target."
              onClick={() => {
                setAirArmed((on) => !on);
                if (!airArmed) say("order", "Click an enemy formation to strike it from the air.");
              }}
            >
              <b>Air support</b>
              <em>✈ {Math.floor(world.air_points)} / {world.air_ceiling ?? "—"}</em>
              <i>{airArmed ? "choose a target…" : world.air_points >= 1 ? "click to strike" : "recharging"}</i>
            </button>
          ) : null}
          <button
            type="button"
            className={`res reinforcements${reinforcementsOpen ? " open" : ""}`}
            title={reinforcementTitle || "No scheduled reinforcements"}
            onClick={() => setReinforcementsOpen((open) => !open)}
          >
            <b>Reinforcements</b>
            <em>
              {nextArrivals.length === 0
                ? "None scheduled"
                : nextArrivals.length === 1
                  ? `${nextArrivals[0].side} · ${nextArrivals[0].name}`
                  : `${nextArrivals.length} formations`}
            </em>
            <i>{nextAt === null ? "—" : formatCampaignTime(nextAt)}</i>
          </button>
          <span className="clock">
            {clock ? (
              <>
                <em>
                  {formatCampaignTime(clock.game_time_s)}
                </em>
                <button type="button" onClick={togglePause} disabled={clockBusy}>
                  {clock.paused ? "▶" : "❚❚"}
                </button>
                {/* ── SPEED ────────────────────────────────────────────────
                    Owner, 2026-09-20: *"Ändra tidsmultiplikatorn till 1x,
                    10x, 25x, 100x."*

                    All four visible rather than one cycling button: this is a
                    slow simulation, and the whole point of the control is
                    jumping straight from watching a battle at 1× to skipping a
                    night at 100×. A cycle button makes 100× four presses away.

                    The server snaps the value it is sent and answers with what
                    it applied, so the highlight follows the *response*. A
                    button that lights up because it was pressed would lie the
                    moment the two disagreed. */}
                <span className="rates">
                  {RATES.map((r) => (
                    <button
                      key={r}
                      type="button"
                      className={rate === r ? "on" : undefined}
                      disabled={clockBusy || clock.paused}
                      title={clock.paused ? "The clock is stopped" : `Run at ${r}×`}
                      onClick={() => void changeRate(r)}
                    >
                      {r}×
                    </button>
                  ))}
                </span>
                {/* The `?` button opened the reference page, which went with
                    `/api/reference`. */}
                <button
                  type="button"
                  className={settingsOpen ? "on" : undefined}
                  title="Theatre settings"
                  onClick={() => setSettingsOpen((v) => !v)}
                >
                  ⚙
                </button>
              </>
            ) : null}
            <button
              type="button"
              className={`imagery${aerial ? " on" : ""}`}
              onClick={() => setAerial((v) => !v)}
              aria-pressed={aerial}
              title={
                aerialInfo ? `Satellite imagery — ${aerialInfo.licence}` : "Satellite imagery"
              }
            >
              {aerial ? "◧ Photo" : "◧ Map"}
            </button>
            <button
              type="button"
              className={`imagery${ams ? " on" : ""}`}
              onClick={() => setAms((v) => !v)}
              aria-pressed={ams}
              title={`The 1943 view: U.S. Army Map Service sheets of 1941–43 — 1:100,000 across the island, 1:50,000 closer in, 1:25,000 closest where they exist. Today's roads on top. Drawn from zoom ${AMS_MIN_ZOOM}.`}
            >
              {ams ? "▦ 1943 on" : "▦ 1943"}
            </button>
          </span>
      </div>

      {/* ── EVERY PLANNED ARRIVAL, BOTH SIDES ─────────────────────────────
          Owner, 2026-09-25: own and enemy. The enemy's timetable is shown on
          purpose — it is his call, taken when asked whether it leaked
          intelligence. Only arrivals still to come are listed; a formation
          that has landed is on the map. A row flies the map to where it
          enters. */}
      {reinforcementsOpen ? (
        <div className="phase-panel reinforcement-panel" role="dialog" aria-label="Planned reinforcements">
          <div className="phase-panel-head">
            <b>Reinforcements · {(world?.arrivals ?? []).length} planned</b>
            <button type="button" onClick={() => setReinforcementsOpen(false)} aria-label="Close">×</button>
          </div>
          {(world?.arrivals ?? []).length === 0 ? (
            <p className="phase-panel-hint">Nothing more is scheduled for either side.</p>
          ) : (
            <p className="phase-panel-hint">Click a row to see where it enters.</p>
          )}
          {(["Allies", "Axis"] as const).map((side) => {
            const rows = (world?.arrivals ?? []).filter((a) => a.side === side);
            if (rows.length === 0) return null;
            return (
              <section key={side} className={`reinforcement-side ${side.toLowerCase()}`}>
                <h4>
                  {side}
                  {mine?.name === side ? " · yours" : mine ? " · enemy" : ""}
                </h4>
                <ol>
                  {rows.map((a) => (
                    <li
                      key={a.event_id}
                      onClick={() => map.current?.flyTo({ center: [a.lon, a.lat], zoom: Math.max(map.current.getZoom(), 10) })}
                    >
                      <span className="when">{formatCampaignTime(a.at_game_s)}</span>
                      <b>{a.name}</b>
                      <span className="how">{a.mode === "air" ? "✈ air" : a.mode === "sea" ? "⚓ sea" : "⛴ strait"}</span>
                      <span className="where">{a.area}</span>
                    </li>
                  ))}
                </ol>
              </section>
            );
          })}
        </div>
      ) : null}

      {phasePanelOpen && campaign?.phases?.length ? (
        <div className="phase-panel" role="dialog" aria-label="Campaign phases">
          <div className="phase-panel-head">
            <b>Operation Husky · six phases</b>
            <button type="button" onClick={() => setPhasePanelOpen(false)} aria-label="Close">×</button>
          </div>
          <p className="phase-panel-hint">
            Hover a phase to see its objectives and front on the map. Take every objective before the phase is
            half over for 🥇, by its end for 🥈, late for 🥉. Formations that stand still dig in.
          </p>
          <ol>
            {campaign.phases.map((p) => {
              const progress = phaseProgress(p);
              return (
                <li
                  key={p.number}
                  className={`${p.current ? "current" : ""}${progress.done ? " done" : ""}${hoveredPhase === p.number ? " hovered" : ""}`}
                  onMouseEnter={() => setHoveredPhase(p.number)}
                  onMouseLeave={() => setHoveredPhase(null)}
                >
                  <div className="phase-row-head">
                    <b>{p.number}. {p.name}</b>
                    <span>{p.dates}</span>
                    {p.medal ? <span className="phase-medal" title={`${p.medal} — all objectives held`}>{medalIcon(p.medal)}</span> : null}
                    {p.current ? <em>now</em> : null}
                  </div>
                  <div className="phase-goal"><span>Allies</span>{p.allied_goal}</div>
                  <div className="phase-goal axis"><span>Axis</span>{p.axis_goal}</div>
                  {p.objectives.length ? (
                    <div className="phase-objectives">
                      {p.objectives.map((o) => (
                        <span key={o.name} className={o.mine ? "held" : ""}>{o.mine ? "✓ " : ""}{o.name}</span>
                      ))}
                      <i>{progress.held}/{progress.total}</i>
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ol>
        </div>
      ) : null}

      {/* ── MESSAGES ─────────────────────────────────────────────────────
          Where the event log stood (owner, 2026-09-25: it took up room). Read,
          never pressed — `pointer-events: none`, the lesson the log's own CSS
          recorded after clicks landed on it and were lost. */}
      {toasts.length > 0 ? (
        <div className="toasts" aria-live="polite">
          {toasts.map((t) => (
            <div
              key={t.id}
              className={`toast ${t.kind}`}
              style={{ animationDuration: `${t.kind === "refused" ? TOAST_REFUSED_MS : TOAST_MS}ms` }}
            >
              {t.text}
            </div>
          ))}
        </div>
      ) : null}

      {/* The attribution is a condition of the licence, not a courtesy, so it
          is rendered whenever the layer is on and cannot be dismissed. */}
      {(aerial && aerialInfo) || ams ? (
        // One line, not two: both are positioned in the same corner.
        <p className="attribution mono">
          {[
            aerial && aerialInfo ? aerialInfo.attribution : null,
            ams ? `${AMS_ATTRIBUTION} · zoom in to see them` : null,
          ]
            .filter(Boolean)
            .join(" | ")}
        </p>
      ) : null}

      {/* ── SETTINGS ───────────────────────────────────────────────────────
          Owner, 2026-09-10: *"ZOC området är för stort. Kan vi fixa en settings
          meny där vi kan sätta hur 'långt' ZOC sträcker sig?"*

          Worth recording why it grew, because the cause was a fix from earlier
          the same day: raising the road-graph stitch reach took the network from
          67% connected to 80%, so the same hourly budget now travels roughly
          twice as far along real roads. The projections were calibrated against
          a broken network.

          Presented as steps rather than a free slider. The value is a
          multiplier on a projection measured in hours — a continuous control
          invites hunting for 0.63, and there is nothing to find there. */}

      {/* ── THE REFERENCE PAGE WENT WITH `/api/reference` ──────────────────
          Owner, 2026-09-10: *"Kan du skapa en sida för tech three där alla
          byggnader listan och vad dom gör. Lika så för enheter."* The answer
          was that **Kartkrig has no tech tree** — `fielded` was an on/off flag
          and the build rules said *where*, not *when* — so it became a
          reference: three tables of buildings, formations and ground with the
          costs, upkeep, recruit prices and yields that governed them.

          Every column in it was an economy figure. What a formation is worth in
          a fight — power, zone, range, support — is still real and still
          unpublished; if that readout is wanted back, it is a smaller page than
          this one was and it should be served rather than written here. */}

      {settingsOpen ? (
        <div className="settings">
          <header>
            <b>Zone of control</b>
            <button type="button" className="close" onClick={() => setSettingsOpen(false)}>
              ×
            </button>
          </header>

          {zocSettings ? (
            <>
              {/* ── THE SHAPE IS TWO THINGS AND THE MENU SAYS SO ───────────
                  Owner, 2026-09-10: *"Det ska vara en grund cirkel där storlek
                  beror på storlek på enhet, till det ska det finnas en spridning
                  ut i terräng."* Grouped the same way, because a single list of
                  five numbers would hide that they answer two different
                  questions. */}
              <div className="group">
                <b>Base circle</b>
                <p className="hint">Held regardless of terrain, by standing there.</p>
                {ZOC_FIELDS.filter((f) => f.group === "base").map((f) => (
                  <label key={f.key} className="row">
                    <span>{f.label}</span>
                    <input
                      type="range"
                      min={f.min}
                      max={f.max}
                      step={f.step}
                      value={zocSettings[f.key]}
                      onChange={(e) =>
                        setZocSettings({ ...zocSettings, [f.key]: Number(e.target.value) })
                      }
                      onPointerUp={(e) =>
                        void changeZoc({ [f.key]: Number(e.currentTarget.value) })
                      }
                      onKeyUp={(e) => void changeZoc({ [f.key]: Number(e.currentTarget.value) })}
                    />
                    <em>{f.format(zocSettings[f.key])}</em>
                  </label>
                ))}
                <p className="hint">
                  A company holds {Math.round(zocSettings.base_radius_m / 100) / 10} km; a
                  division{" "}
                  {Math.round(
                    (zocSettings.base_radius_m *
                      Math.pow(1 + zocSettings.step_per_echelon, 4)) /
                      100,
                  ) / 10}{" "}
                  km.
                </p>
              </div>

              <div className="group">
                <b>Spread</b>
                <p className="hint">
                  How far a theoretical formation gets outward in the time given.
                </p>
                {ZOC_FIELDS.filter((f) => f.group === "spread").map((f) => (
                  <label key={f.key} className="row">
                    <span>{f.label}</span>
                    <input
                      type="range"
                      min={f.min}
                      max={f.max}
                      step={f.step}
                      value={zocSettings[f.key]}
                      onChange={(e) =>
                        setZocSettings({ ...zocSettings, [f.key]: Number(e.target.value) })
                      }
                      onPointerUp={(e) =>
                        void changeZoc({ [f.key]: Number(e.currentTarget.value) })
                      }
                      onKeyUp={(e) => void changeZoc({ [f.key]: Number(e.currentTarget.value) })}
                    />
                    <em>{f.format(zocSettings[f.key])}</em>
                  </label>
                ))}
                <p className="hint">
                  Reaches about{" "}
                  {Math.round(zocSettings.road_kmh * zocSettings.spread_hours)} km by road,{" "}
                  {Math.round(zocSettings.terrain_kmh * zocSettings.spread_hours)} km across
                  open ground.
                </p>
              </div>

              {/* Said plainly because it is the constraint he stated, and
                  because a menu full of km/h invites the opposite assumption. */}
              <p className="hint note">
                These change the zone only. March speed is unaffected.
              </p>

              {/* ── THE CENSUS ────────────────────────────────────────────
                  Phase A: what the ground inside this zone is made of. It
                  changes nothing — no income, no balance — and is here because
                  the zone is its subject. */}
              {/* The census panel stood here: ground classes as bars, the
                  largest settlement, how much of the zone was classified, and
                  what the ground earned per hour once buildings were counted. It
                  went with `/api/census`. */}
            </>
          ) : (
            <p className="hint">Reading the theatre&rsquo;s settings…</p>
          )}
        </div>
      ) : null}

      {/* ── WHAT A FORMATION IS DOING, ON HOVER ────────────────────────────
          Supply and current order. Nothing else — this is a glance, not a
          panel, and everything else about the formation is already on the map:
          its identity under the symbol, its strength beside that when there is
          any to report, its route drawn ahead of it. */}
      {battle ? (
        <aside className="battlereport">
          <header>
            <b>{battle.standoff ? "Standoff" : "Battle report"}</b>
            <span>
              round {battle.rounds} · {battle.hours.toFixed(1)} h
            </span>
          </header>
          {/* Momentum is the reading of the exchange, not a prediction. "Even"
              is a real answer — a stalemate reported as a win is a lie the
              player acts on. */}
          {battle.standoff ? (
            <p className="standoff">
              Neither side is attacking. Both hold their ground and lose little;
              the Assault stance turns this into an attack.
            </p>
          ) : null}
          <p className={`momentum ${battle.momentum?.toLowerCase() ?? "even"}`}>
            {battle.momentum === null
              ? "Neither side is gaining"
              : `${battle.momentum} has the momentum`}
          </p>
          <div className="force-comparison">
            {battle.forces.map((force) => (
              <div key={force.side} className={force.side.toLowerCase()}>
                <b>{force.side}</b>
                <span>power {force.combat_power.toFixed(1)}</span>
                <span>losses −{Math.round(force.strength_lost * 100)}%</span>
                {force.formations_lost > 0 ? <span>{force.formations_lost} destroyed</span> : null}
              </div>
            ))}
          </div>
          <ul>
            {battle.sides
              .slice()
              .sort((a, b) => Number(b.defending) - Number(a.defending))
              .map((s) => {
                const lost =
                  s.strength_at_start === null
                    ? null
                    : Math.max(0, s.strength_at_start - s.strength);
                const defender = battle.sides.find((candidate) => candidate.defending);
                const advice = !s.defending && defender && s.elevation_m < defender.elevation_m - 30
                  ? "Seek higher ground before pressing: this formation is attacking uphill."
                  : ["armour", "mechanised", "reconnaissance"].includes(s.branch) && ["forest", "urban", "wetland"].includes(s.ground)
                    ? "Move mobile troops toward open ground or a road; this terrain channels them."
                    : s.defending && ["open", "wetland"].includes(s.ground)
                      ? "A forest, town or steeper position would be easier to hold. Moving during contact requires retreating first."
                      : s.readiness < 0.7
                        ? "Readiness is limiting combat power; break contact and rest in supply."
                        : null;
                return (
                  <li key={s.unit_id} className={s.mine ? "mine" : "theirs"}>
                    <span className="who">
                      {s.side} · {s.name} · {s.defending ? "holding" : "pressing"}
                    </span>
                    <span className="str">
                      {Math.round(s.strength * 100)}%
                      {lost !== null && lost > 0.001 ? (
                        <i> −{Math.round(lost * 100)}</i>
                      ) : null}
                    </span>
                    <span className="ground">{s.ground} · {Math.round(s.elevation_m)} m · {s.stance.replaceAll("_", " ")} · power {s.combat_power.toFixed(1)}</span>
                    <span className="factors">
                      {s.factors.map((factor) => (
                        <i key={`${s.unit_id}-${factor.label}`} className={factor.multiplier >= 1 ? "bonus" : "penalty"}>
                          <b>{factor.label} ×{factor.multiplier.toFixed(2)}</b>
                          <small>{factor.explanation}</small>
                        </i>
                      ))}
                    </span>
                    {advice ? <span className="advice">Opportunity · {advice}</span> : null}
                  </li>
                );
              })}
          </ul>
        </aside>
      ) : null}

      {/* Owner, 2026-09-12: *"inte heller annan info om enhet utöver typ och
          nivå."* The server now redacts an enemy formation, so this card would
          have little left to show — but it would still name it and still claim
          a supply state. The symbol and its caption already carry type and
          level, which is the whole of what he allows. */}
      {/* ── AND IDENTIFICATION OPENS IT, WHICH IS THE NEW HALF ──────────────
          Owner, 2026-09-17: an enemy's unit information shows when it is
          identified. So the gate is three-state, not two: your own formation
          gets the whole card, an identified enemy gets what identification
          buys — name, supply and condition — and one that is merely seen still
          gets nothing, because the symbol and its caption already carry type
          and level, which is the whole of what he allowed on 2026-09-12.

          The order line stays on your own formations only. It reads out a
          destination and an arrival time, and the march route is the half he
          named first and never took back — the server withholds it at every
          grade, so there would be nothing to print anyway. */}
      {hoverUnit && hover && (hoverUnit.mine || hoverUnit.identified) ? (
        <div className="unithover" style={{ left: `${hover.x}px`, top: `${hover.y}px` }}>
          <div className="uh-head">
          <b>{hoverUnit.name}</b>
          <span className={`supply ${hoverUnit.supply}`}>{hoverUnit.supply}</span>
          </div>
          {/* ── THE ETA SAYS BOTH TIMES WHEN THEY DIFFER ─────────────────────
              Owner, 2026-09-10: *"Jag tycker inte det är någon skillnad mellan
              1x och 10x .. både ger samma tidsuppskattning till enhet är
              framme."*

              He was right twice over. Movement was running on the wall clock,
              so speed genuinely did nothing — fixed on the server. But the
              figure itself is *game* time and stays the same at every speed,
              which is correct and still not the answer to "how long do I
              wait?". At 1× the two are the same and only one is shown; above
              it, the wall-clock wait is added in brackets. */}
          {hoverUnit.mine ? (
            <span className="order">
              {hoverUnit.withdrawing
                ? `${(hoverUnit.starts_in_s ?? 0) > 0 ? "disengaging under fire" : "retreating"}${hoverUnit.eta_s !== null ? ` — ${formatRemaining(hoverUnit.eta_s)}${rate > 1 ? ` (${formatRemaining(hoverUnit.eta_s / rate)} real)` : ""}` : ""}`
                : hoverUnit.engaged
                ? "fighting — only withdraw orders are available"
                : hoverUnit.progress !== null
                ? hoverUnit.eta_s !== null
                  ? `${hoverUnit.withdrawing ? (hoverUnit.starts_in_s ?? 0) > 0 ? "disengaging" : "retreating" : "moving"} — ${formatRemaining(
                      hoverUnit.eta_s,
                    )}${
                      rate > 1 ? ` (${formatRemaining(hoverUnit.eta_s / rate)} real)` : ""
                    }`
                  : "moving"
                : "holding"}
            </span>
          ) : null}
          {/* ── CONDITION AND WHAT THE BADGES MEAN ──────────────────────────
              Owner, 2026-09-25: *"När jag hovrar över en enhet vill jag att
              statusikon som är satt på enhet förklaras med text. Jag vill även
              ha info om enhetens status gällande styrka, ammo, fuel och
              readiness."* An identified enemy's condition is unit information
              (2026-09-17) and shows too; its badges come from the same
              `statusOf` the symbol is drawn from, so the card cannot explain
              a badge the map does not show. */}
          <div className="uh-stats">
            {(
              [
                ["Strength", hoverUnit.strength],
                ["Readiness", hoverUnit.readiness],
                ["Fuel", hoverUnit.fuel],
                ["Ammo", hoverUnit.ammunition],
              ] as [string, number][]
            ).map(([label, value]) => (
              <span key={label}>
                {label} <b style={{ color: strengthColour(value) }}>{Math.round(value * 100)}%</b>
              </span>
            ))}
          </div>
          {(() => {
            const notes = explainStatus(statusOf(hoverUnit), hoverUnit);
            return notes.length ? (
              <ul className="uh-badges">
                {notes.map((n) => (
                  <li key={n.badge}>
                    <i style={{ background: n.colour }} aria-hidden="true" />
                    {n.text}
                  </li>
                ))}
              </ul>
            ) : null;
          })()}
        </div>
      ) : null}

      {/* ── THE UNIT MENU, IN KARTBORD'S PATTERN ───────────────────────────
          Owner, 2026-09-10: *"Kika på hur vi gör menyn för pjäser i kartbord
          och gör en liknande meny för våra enheter."*

          Read out of `kartbord/index.html` (`showMenu` / `pieceActions`) and
          four things carried over:

            1. **A heading naming the thing**, with its state appended — a piece
               menu there reads "Blue rook · face down", so this one reads
               "1 Recon · marching".
            2. **A vertical list of rows, not a grid of buttons.** The old
               version put every action in a wrapped block, so a headquarters
               showed ten buttons of varying width and nothing could be found
               twice in the same place.
            3. **Submenus for the sets.** Recruit and Build were six of those
               ten buttons; they collapsed to two rows with a `›`, which is what
               made the menu short enough to read at a glance. ⚠️ Both submenus
               went with the RTS layer — one building is left and nothing is
               raised — so the mechanism is gone while the lesson stands: the
               moment a set grows past two entries it folds rather than sprawls.
            4. **Dead rows that explain themselves.** Kartbord's own comment is
               the reason: *«menyn ska SÄGA varför den är tom, inte bara vara
               tom»*. An infantry company still sees a greyed line saying whose
               job it is, instead of the row simply being absent.

          What is *not* carried over: Kartbord opens submenus on hover as well
          as click. That is right for a desktop board and wrong here — this is
          played on a phone, where there is no hover and an accidental drag past
          a row would open a submenu under the thumb. Click only. */}
      {menuUnit && menuAt ? (
        <div
          className="unitmenu"
          style={{ left: `${menuAt.x}px`, top: `${menuAt.y}px` }}
          role="menu"
        >
          <div className="head">
            {menuUnit.name}
            <span>
              {" · "}
              {menuUnit.withdrawing ? (menuUnit.starts_in_s ?? 0) > 0 ? "disengaging under fire" : "retreating" : menuUnit.engaged ? "fighting — can only pull back" : menuUnit.progress !== null ? "moving" : "holding"}
            </span>
          </div>

          {/* ── MOVE AND FORCE MOVE ─────────────────────────────────────────
              Owner, 2026-09-25: *"Ändra så att det i menyn bara finns 'Move'
              och 'Force move'. Move väljer kortaste restiden och Force move
              väljer kortaste vägen (genom terräng)."*

              Replaced three buttons: Move (the quicker of road and ground),
              Move by road and Move cross-country (owner, 2026-09-15). Move is
              unchanged. Force move is the old cross-country order: the direct
              line over the ground, bent only by water, slopes too steep to
              climb and ground that slows the column. Nothing forces the road
              any more — Move takes it whenever it is quicker.

              Owner, 2026-09-12: *"'Move' i menyn ska ersättas av 'Retreat'
              när en enhet är i strid."* Still in force for both, since the
              server will only allow a pull-back: the head line above already
              says "can only pull back", and the buttons agree with it. */}
          {menuUnit.branch !== "headquarters" ? (
            <>
              <button type="button" disabled={acting} onClick={() => void runAction("move")}>
                {menuUnit.engaged ? "Retreat" : "Move"}
              </button>
              <button
                type="button"
                disabled={acting}
                onClick={() => void runAction("move", "ground")}
              >
                {menuUnit.engaged ? "Force retreat" : "Force move"}
              </button>
            </>
          ) : (
            <div className="dead">A headquarters does not march</div>
          )}

          <div className="group-label">Combat stance · {menuUnit.combat_stance.replaceAll("_", " ")}</div>
          <button type="button" disabled={acting || menuUnit.combat_stance === "balanced"} onClick={() => void runAction("stance", "balanced")}>
            Balanced · no modifier
          </button>
          <button type="button" disabled={acting || menuUnit.combat_stance === "hold_ground"} onClick={() => void runAction("stance", "hold_ground")}>
            Hold ground · +20% defence, −15% attack
          </button>
          <button type="button" disabled={acting || menuUnit.combat_stance === "assault"} onClick={() => void runAction("stance", "assault")}>
            Assault · +20% attack, −15% defence
          </button>
          <button type="button" disabled={acting || menuUnit.combat_stance === "delay"} onClick={() => void runAction("stance", "delay")}>
            Delay · +10% defence, −15% attack, ½ disengagement
          </button>

          {/* Only when there is something to join with: an action whose answer
              is always no is not an action. */}
          {joinCandidates.length > 1 ? (
            <button type="button" disabled={acting} onClick={() => void runAction("join")}>
              Join {joinCandidates.length}
            </button>
          ) : null}

          {menuUnit.echelon !== "company" ? (
            <button type="button" disabled={acting} onClick={() => void runAction("split")}>
              Split
            </button>
          ) : null}

          {/* The Refit button went with the mechanic — it showed only when there
              was damage, and there is nothing to spend on damage now. A
              formation's strength is what the campaign has left it.

              ⚠️ Worth a readout later rather than a button: a player can still
              see the percentage on the symbol, but nothing tells them that
              standing still in supply is what brings readiness back. */}

          {/* ── ONE THING GOES UP, SO THERE IS NO SUBMENU ───────────────────
              "Recruit here" opened a list of four branches; "Build" opened a
              list of five buildings filtered by who could put each one up. Both
              followed `Branch::may` on the server, and the row was shown greyed
              with its reason rather than hidden — a rule the player can read
              beats a button that is simply absent.

              That reasoning is kept: a supply company gets the action, everyone
              else gets the line saying whose job it is. What went is the
              submenu, because a menu with one entry is a click in the way. */}
          {/* The button itself went with the supply points. The reasoning above
              is kept because it is about menus, not about buildings: a row shown
              greyed with its reason beats a row that is simply absent. */}
        </div>
      ) : null}

      {world ? (
        <div className="theatrebar">
          <span className="mono">{world.name}</span>
          <button
            type="button"
            className="game-code mono"
            title="Copy game code"
            onClick={() => void navigator.clipboard?.writeText(gameCode)}
          >
            {gameCode}
          </button>
          {/* Contested ground stays: it is a standing fact about the board, not
              an event. The zone note moved to the log — see `lastZoneNote`. */}
          {zoc ? (
            <span className="mono dim">
              {zoc.zones.length} zones
              {zoc.contested_area ? " · contested ground" : ""}
            </span>
          ) : null}
          <button type="button" className="home" onClick={() => onLeave(false)}>
            Home
          </button>
          <button
            type="button"
            className={`danger${confirmReset ? " armed" : ""}`}
            onClick={() => void resetGame()}
            disabled={resetting}
          >
            {resetting ? "Resetting…" : confirmReset ? "Click again to delete" : "Reset"}
          </button>
        </div>
      ) : null}

    </div>
  );
}

const SAVED_GAME_CODE = "kartkrig.game-code";

/** The map is mounted only after a code has been created or verified. */
export default function App() {
  const [activeCode, setActiveCode] = useState<string | null>(null);
  const [newGame, setNewGame] = useState(false);
  const [code, setCode] = useState(() => localStorage.getItem(SAVED_GAME_CODE) ?? "");
  const [busy, setBusy] = useState<"start" | "continue" | null>(null);
  const [error, setError] = useState<string | null>(null);

  const open = (nextCode: string, freshlyCreated = false) => {
    const normalised = nextCode.trim().toLowerCase();
    setGameCode(normalised);
    localStorage.setItem(SAVED_GAME_CODE, normalised);
    setCode(normalised);
    setActiveCode(normalised);
    setNewGame(freshlyCreated);
  };

  const start = async () => {
    setBusy("start");
    setError(null);
    setGameCode(null);
    try {
      const created = await createWorld(37.07, 14.25, "Operation Husky");
      open(created.game_code, true);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not start a new game");
    } finally {
      setBusy(null);
    }
  };

  const resume = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy("continue");
    setError(null);
    try {
      const found = await continueGame(code);
      open(found.game_code);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not find that game");
    } finally {
      setBusy(null);
    }
  };

  const leave = (forget: boolean) => {
    setGameCode(null);
    setActiveCode(null);
    setNewGame(false);
    if (forget) {
      localStorage.removeItem(SAVED_GAME_CODE);
      setCode("");
    }
  };

  if (activeCode) {
    return <Game key={activeCode} gameCode={activeCode} onLeave={leave} briefing={newGame} />;
  }

  return (
    <main className="landing">
      <section className="landing-card">
        <p className="landing-kicker">Operational command · Sicily 1943</p>
        <h1>Kartkrig</h1>
        <p className="landing-intro">
          Command the Allied invasion of Sicily. Your campaign keeps running on
          the server and is opened with one private game code—no account needed.
        </p>

        <button
          type="button"
          className="start-game"
          disabled={busy !== null}
          onClick={() => void start()}
        >
          {busy === "start" ? "Preparing Sicily…" : "Start new game"}
        </button>

        <div className="landing-divider"><span>or</span></div>

        <form onSubmit={(event) => void resume(event)}>
          <label htmlFor="game-code">Game code</label>
          <div className="continue-row">
            <input
              id="game-code"
              value={code}
              onChange={(event) => setCode(event.target.value.toLowerCase())}
              placeholder="gc-word1-word2-word3"
              autoComplete="off"
              autoCapitalize="none"
              spellCheck={false}
            />
            <button type="submit" disabled={busy !== null || code.trim() === ""}>
              {busy === "continue" ? "Opening…" : "Continue game"}
            </button>
          </div>
          {localStorage.getItem(SAVED_GAME_CODE) ? (
            <small>The last game used on this device is filled in for you.</small>
          ) : null}
        </form>
        {error ? <p className="landing-error" role="alert">{error}</p> : null}

        <p className="landing-note">
          Keep the code safe. Anyone who has it can open and command this game.
        </p>
      </section>
    </main>
  );
}
